// Phase 17 Plan 05 Task 2 — MatchScreen rebuilt to UI-SPEC §6.2 Layout A
// composition. Visual shell adopts the rg-match.jsx prototype (rg-screen.match
// main + MatchTopBar + Board column + MatchBottomBar + PhaseControls + SUBMIT
// TURN Sun-Yellow Btn). The Phase 14 gameplay plumbing — WS subscription via
// useMatchData, $turnBuilder consumption, WordTraceLayer + PieceDragLayer
// pointer overlays, MatchGrid parallel-DOM a11y surface, MatchCanvas PixiJS
// rendering — is preserved verbatim. Only the chrome and the 4 new overlay
// components (EmpowerModal, FlopReveal, QueenRises, WinOverlay) change shape.
//
// Plumbing preserved (DO NOT MODIFY without re-verifying Phase 14 contracts):
//   - useMatchData(matchId): WS + polling fallback + submitTurn
//   - useStore($turnBuilder): per-turn draft state
//   - useStore($onboarding): hydrated from /api/profile/me
//   - clearTurnBuilder() on TURN_APPLIED (current_turn_idx transition)
//   - matchStatus terminal-state detection (7 statuses) + runegambit:match-
//     completed dispatch
//   - Phase B selection routing (handlePhaseBSelect): empower via the role-first
//     stage machine (beginEmpower / onEmpowerPieceTap), power via the PowerupTray
//     gate — both driven directly, no window events
//   - submitTurn payload assembly from live $turnBuilder.get()
//   - PowerupTray gate (Phase B power)  — Phase 24 RUNE-05 deleted Phase A 'swap'
//   - End-of-match FriendInviteModal (real matches) — listener fires on the
//     match-completed dispatch. ARCA-147: the bot PushOptInModal is no longer
//     fired here — it is deferred to LobbyScreen via a sessionStorage baton set
//     in navigateHome so it never lands over the terminal result overlay.
//
// Render-layer additions (Plan 17-05):
//   - MatchTopBar (Plan 17-04 primitive) — replaces ./TopBar (deleted in Task 1)
//   - PhaseControls (Plan 17-04 primitive) — replaces ./PhaseAPills + ./PhaseBPills
//     (deleted in Task 1). Composes the existing $turnBuilder discriminated
//     union via local adapter functions.
//   - EmpowerModal (Plan 17-05 Task 1) — replaces ./EmpowerBottomSheet (deleted)
//   - FlopReveal / QueenRises / WinOverlay (Plan 17-05 Task 2) — DOM overlays
//     mirroring the rg-match.jsx prototype
//   - MatchBottomBar (Plan 17-04 primitive) — UI-SPEC §6.2 row 7
//   - Btn (take-two primitive) — SUBMIT TURN primary, full-width (block)

import { useStore } from '@nanostores/react'
import {
  type JSX,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { AuthInvalidError } from '../../auth/types'
import { playSfx } from '../../game/audio/useAudio'
import { type BoardView, DEFAULT_BOARD_VIEW } from '../../game/board-view'
import {
  BOARD_COLS,
  boardRows,
  CELL_SIZE_PX,
  CHESS_ROWS,
  centerGrowWindow,
} from '../../game/layers/board-layout'
import type { PlayerView, PowerUpName } from '../../game/PlayerViewSchema'
import type { RevealKind } from '../../game/reveals/timing'
import { useDocumentTitle } from '../../i18n/useDocumentTitle'
import { apiFetch } from '../../net/client'
import { fetchHome } from '../../net/home'
import { placeAnchor } from '../../net/place-anchor'
import { toChessStepBody } from '../../net/turn-submit'
import { onEmoteReceived, sendEmote } from '../../net/ws-client'
import { useRouter } from '../../router/router'
import { isAnonPracticeSession } from '../../stores/anonSession'
import { $boardReanchor } from '../../stores/board-reanchor'
import { $lobbyData } from '../../stores/lobbyData'
import { $reducedMotion } from '../../stores/motion'
import {
  $onboarding,
  incrementMatchesPlayed,
  setPendingBotPushOptIn,
  setRealMatchesPlayedCache,
} from '../../stores/onboarding'
import { emitParticleBurst } from '../../stores/particleBus'
import {
  $turnBuilder,
  clearPhaseA,
  clearTurnBuilder,
  setPhaseA,
  shouldDefaultPhaseAToWord,
  stageEmpower,
  stageExtend,
  stageMove,
  stagePowerup,
} from '../../stores/turnBuilder'
import { COACH_TIP_Z_INDEX, CoachTip } from '../coach/CoachTip'
import { FindableWordOutline } from '../coach/FindableWordOutline'
import { HintButton } from '../coach/HintButton'
import { useCoachHint } from '../coach/useCoachHint'
import {
  type FirstRunHintCandidate,
  useFirstRunCoachHints,
} from '../coach/useFirstRunCoachHints'
import { Btn } from '../components/Btn'
import { MatchTopBar } from '../components/MatchTopBar'
import { type PhaseAValue, PhaseControls } from '../components/PhaseControls'
import { TokenBank } from '../components/TokenBank'
import { useAnnounce } from '../components/useAnnounce'
import { AffinityChipRow } from './AffinityChipRow'
import { AnchorPlacementOverlay } from './AnchorPlacementOverlay'
import { AnchorPlacementPrompt } from './AnchorPlacementPrompt'
import { ChessControlDeck } from './ChessControlDeck'
import { type EmpowerableRole, EmpowerModal } from './EmpowerModal'
import { ExtendModal } from './ExtendModal'
import { EmotePicker } from './emotes/EmotePicker'
import { type ActiveSticker, StickerOverlay } from './emotes/StickerOverlay'
import { FirstAnchorTutorialModal } from './FirstAnchorTutorialModal'
import { FlopReveal } from './FlopReveal'
import { FriendInviteModal } from './FriendInviteModal'
import { resolveGrowRows } from './grow-rows'
import { MatchCanvas } from './MatchCanvas'
import { MatchGrid } from './MatchGrid'
import { MulliganSelector } from './MulliganSelector'
import { resolveMatchChromePx } from './match-chrome'
import {
  anchorFailureMessageIds,
  submitErrorMessageId,
} from './match-error-copy'
import { deriveTerminalOutcome } from './match-outcome'
import { PieceDragLayer } from './PieceDragLayer'
import { PowerupActivateOverlay } from './PowerupActivateOverlay'
import { PowerupTargetSelector } from './PowerupTargetSelector'
import { PowerupTray } from './PowerupTray'
import { QueenRises } from './QueenRises'
import { type QuillMode, QuillModePicker } from './QuillModePicker'
import { QuillTargetOverlay, QuillTargetPrompt } from './QuillTargetOverlay'
import { RiverReveal } from './RiverReveal'
import { ScorePillVerdict } from './ScorePillVerdict'
import { StagedWordChip } from './StagedWordChip'
import {
  chessViewPlayerView,
  stagedMoveEmpowerConflict,
  stagedMoveFromPhaseB,
} from './staged-preview'
import { TokenFly } from './TokenFly'
import { useMatchData } from './useMatchData'
import { WinOverlay, type WinOverlayLevelUpData } from './WinOverlay'
import { WordTraceLayer } from './WordTraceLayer'

// Plan 14-05 (MUI-05) — the legacy findable-words feature-flag constant was
// deleted in v1.3 per D-COACH-11 (hard cutover). The findable-word overlay
// + the new CoachTip + cooldown toast are now driven by `useCoachHint()`
// state below; render-gating is "phase-matching" + the same realMatchesPlayed
// < 5 ceiling the HintButton enforces (D-COACH-01 / D-COACH-09).

type MatchMode = 'casual' | 'ranked' | 'friend' | 'bot' | 'tournament_round'

type MatchCompletedDetail = {
  mode: MatchMode
}

const VALID_MATCH_MODES: ReadonlySet<MatchMode> = new Set<MatchMode>([
  'casual',
  'ranked',
  'friend',
  'bot',
  'tournament_round',
])

// ARCA-155 — top padding of the board-stack wrapper (the `padding: '8px 16px 0'`
// below). The board's first cell row begins this far below the board-stack's
// bounding-box top, so the measured `--board-top` adds it to land the
// PowerupTargetSelector overlay grid on board grid row 0. Keep in sync with the
// board-stack `paddingTop`.
const BOARD_STACK_PAD_TOP = 8

function isMatchMode(x: unknown): x is MatchMode {
  return typeof x === 'string' && VALID_MATCH_MODES.has(x as MatchMode)
}

/**
 * ANCHOR-08 (ARCA-143 / ARCA-120) — how long the AnchorPlacementPrompt holds its
 * "Anchor placed!" confirmation copy after a successful placement. The overlay +
 * prompt would otherwise vanish the instant the next server snapshot clears
 * `requires_anchor_placement`, leaving only the 4px optimistic dot as feedback;
 * this latch guarantees a clearly-visible success beat regardless of how fast
 * the snapshot arrives.
 */
const ANCHOR_PLACED_CONFIRM_MS = 800

interface MatchScreenProps {
  matchId: string
}

// ARCA-145 (Bug B) — the submit-error banner auto-dismisses on this timer so a
// stale error never lingers over the control deck. Dismissal MUST NOT depend on
// the next Submit press (the old soft-lock); see the auto-dismiss effect below.
const SUBMIT_ERROR_AUTO_DISMISS_MS = 5000

/**
 * Empower clarity (role-first flow) — the empower interaction's local stage:
 *   'idle'   — not empowering
 *   'role'   — EmpowerModal open, picking a role
 *   'arming' — role chosen; board glows the caller's empowerable pieces and
 *              waits for a piece tap to commit.
 */
type EmpowerStage = 'idle' | 'role' | 'arming'

/**
 * Adapter — maps the $turnBuilder phaseA discriminated union to the
 * PhaseControls primitive's PhaseAValue scalar ('word' | 'skip').
 * Returns null when no Phase A intent has been set.
 *
 * Phase 24 (RUNE-05) deleted the legacy 'swap' arm.
 */
function phaseAFromBuilder(
  phaseA: ReturnType<typeof $turnBuilder.get>['phaseA'],
): PhaseAValue | null {
  if (phaseA === null) return null
  return phaseA.kind
}

/**
 * Adapter — applies a PhaseAValue selection back into the $turnBuilder store.
 * Mirrors the old PhaseAPills routing rules (Plan 14-02), narrowed to the
 * Phase 24 (RUNE-05) Submit | Skip surface:
 *   - word → setPhaseA({kind:'word', word:'', path:[]})
 *   - skip → setPhaseA({kind:'skip'}) (with soft-clear toast if prior path had
 *           >0 cells)
 */
function applyPhaseA(v: PhaseAValue): void {
  const cur = $turnBuilder.get().phaseA
  if (v === 'word') {
    setPhaseA({ kind: 'word', word: '', path: [] })
    return
  }
  if (cur?.kind === 'word' && cur.path.length > 0) {
    window.dispatchEvent(
      new CustomEvent('runegambit:toast', {
        detail: { id: 'match.phase_a.cleared' },
      }),
    )
  }
  setPhaseA({ kind: 'skip' })
}

/**
 * Decorative atmosphere motes — 16 absolutely-positioned spans behind the
 * Match content, color-cycled between magic / cta-primary / player-a.
 * pointer-events:none + aria-hidden:true so they never block interaction or
 * pollute the a11y tree.
 */
function Motes(): JSX.Element {
  const motes = Array.from({ length: 16 }, (_, i) => {
    const left = (i * 37) % 100
    const top = (i * 53) % 100
    const size = 3 + ((i * 7) % 4)
    const color =
      i % 3 === 0
        ? 'var(--color-magic)'
        : i % 3 === 1
          ? 'var(--color-cta-primary)'
          : 'var(--color-player-a)'
    return (
      <span
        // biome-ignore lint/suspicious/noArrayIndexKey: 16 fixed positions, decorative
        key={i}
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: `${left}%`,
          top: `${top}%`,
          width: size,
          height: size,
          borderRadius: 'var(--radius-pill)',
          background: color,
          boxShadow: `0 0 ${size * 2}px currentColor`,
          color,
          opacity: 0.5,
        }}
      />
    )
  })
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {motes}
    </div>
  )
}

export function MatchScreen({ matchId }: MatchScreenProps): JSX.Element {
  useDocumentTitle('doc.title.match')
  const {
    playerView,
    submitWord,
    submitChess,
    applyLocalView,
    status,
    matchStatus,
  } = useMatchData(matchId)
  const turn = useStore($turnBuilder)
  const onboarding = useStore($onboarding)
  const boardReanchorEpoch = useStore($boardReanchor)
  const reducedMotion = useStore($reducedMotion)
  const intl = useIntl()
  const router = useRouter()
  // Phase 18 Plan 04 (POLISH-07) — global aria-live announcer. Replaces the
  // inline aria-live="polite" wrappers that lived around submitError and the
  // end-of-match banner; adds a transient turn-state announcement keyed on
  // current_player_slot transitions.
  const announce = useAnnounce()

  // Phase v1.3 (MUI-05 / D-COACH-*) — on-demand coach hint state machine.
  // The hook owns the {idle/loading/findable_word/powerup_timing/cooldown/
  // none/error} state; MatchScreen renders the surface from it (below) and
  // wires the HintButton (`hintSlot` on MatchTopBar) to `requestHint()`. A
  // dedicated effect resets the state when `current_turn_idx` advances so a
  // stale findable-word outline doesn't bleed into the next turn.
  const {
    state: hintState,
    request: requestHint,
    dismiss: dismissHint,
  } = useCoachHint(matchId)
  const turnIdxForReset = playerView?.current_turn_idx ?? null
  useEffect(() => {
    // Reset on EVERY turn transition. The hook idle-resets internally on
    // dismiss() — calling it from this effect ties the lifecycle to the
    // server's authoritative turn counter (D-COACH-05 cooldown is per-turn).
    if (turnIdxForReset !== null) dismissHint()
  }, [turnIdxForReset, dismissHint])

  // Onboarding clarity — first-run contextual coach hints (tutorial-INDEPENDENT).
  // These fire in the LIVE match the first time a core system becomes usable,
  // pointing the player at the now self-documenting surface. Gated by
  // realMatchesPlayed (< 5) + one-shot — NOT by tutorial completion, so a
  // player who skipped the tutorial is still taught. The candidates' `active`
  // flags derive purely from the authoritative PlayerView.
  const firstRunHintCandidates = useMemo<FirstRunHintCandidate[]>(() => {
    if (playerView === null) return []
    const myTurn = playerView.current_player_slot === playerView.your_slot
    // Does the in-progress word trace cross one of the caller's affinity runes?
    // (i.e. the live ScorePill is showing the cryptic "+Aff" token.) That is
    // the exact moment to decode the abbreviation for a newcomer.
    const tracePath = turn.phaseA?.kind === 'word' ? turn.phaseA.path : []
    const affinitySet = new Set<string>(playerView.your_affinity_letters)
    const traceHasAffinity =
      tracePath.length > 0 &&
      tracePath.some((idx) => {
        const cell = playerView.board.find((c) => c.cell_idx === idx)
        return cell?.letter != null && affinitySet.has(cell.letter)
      })
    // AFFINITY-05 — a haloed cell (one of your rune letters) is on the board.
    const boardHasAffinityHalo = playerView.board.some(
      (c) => c.letter != null && affinitySet.has(c.letter),
    )
    return [
      {
        key: 'first_use_affinity_halo',
        copyKey: 'coach.first_use.affinity_halo',
        // Proactively connect the cream board glow to the chip row + the +1
        // payoff, the first turn one of your rune letters is on the field.
        active: myTurn && boardHasAffinityHalo,
      },
      {
        key: 'first_use_affinity_bonus',
        copyKey: 'coach.first_use.affinity_bonus',
        // First trace that crosses an affinity rune → decode the "+Aff"
        // score-pill token at the moment it appears.
        active: myTurn && traceHasAffinity,
      },
      {
        key: 'first_use_empower',
        copyKey: 'coach.first_use.empower',
        // You have a token and no active role on your turn → empower is the
        // next meaningful move and the least discoverable one.
        active:
          myTurn &&
          (playerView.your_tokens ?? 0) >= 1 &&
          playerView.your_role == null,
      },
      {
        key: 'first_use_powerup',
        copyKey: 'coach.first_use.powerup',
        // A power-up is usable on your turn → introduce the whole system once.
        active: myTurn && (playerView.available_power_ups?.length ?? 0) >= 1,
      },
    ]
  }, [playerView, turn.phaseA])
  const { activeHint: firstRunHint, dismiss: dismissFirstRunHint } =
    useFirstRunCoachHints(firstRunHintCandidates, onboarding.realMatchesPlayed)

  const [showFriendInvite, setShowFriendInvite] = useState(false)
  // GAP-114 (Phase 21 milestone audit BLOCKER-02 fix) — the real mode of this
  // match, resolved at mount from $lobbyData.active_matches (or a fresh
  // /api/home fetch). Mode flows from the OpenAPI `ActiveMatchPreview.mode`
  // field (canonical source — backed by the matches table's `mode` column).
  // Closes the prior TODO(WR-01) which short-circuited PushOptInModal /
  // FriendInviteModal routing by hardcoding 'bot' for every terminal-state
  // dispatch. Null until resolved — the match-completed dispatch defers to
  // `matchMode ?? null`; the listener early-returns on null so neither
  // end-of-match modal fires for an unresolved mode (safer than guessing).
  const [matchMode, setMatchMode] = useState<MatchMode | null>(null)
  const [reveal, setReveal] = useState<RevealKind | null>(null)
  const [queenRisesActive, setQueenRisesActive] = useState(false)
  // ARCA-154 — race-free "a grow cinematic is animating this grow" signal for
  // resolveGrowRows. `reveal !== null` lags one render behind the grow (it is
  // set in the turn-idx effect below, which runs in the same commit the board
  // grows, but as a setState it only lands next render), so reading it directly
  // in the grow-rows effect would let the safety-net snap fire during a real
  // cinematic and skip the breathe-open. This ref is set synchronously when the
  // reveal is triggered (the turn-idx effect runs BEFORE the grow-rows effect in
  // the same commit) and cleared on dismiss, closing that one-render gap.
  const growCinematicInFlightRef = useRef(false)
  const dismissReveal = useCallback(() => {
    growCinematicInFlightRef.current = false
    setReveal(null)
  }, [])
  const dismissQueenRises = useCallback(() => {
    setQueenRisesActive(false)
  }, [])

  const [boardView, setBoardView] = useState<BoardView>(DEFAULT_BOARD_VIEW)
  const toggleBoardView = useCallback(() => {
    // §6.4 board flip — woody whump + settle thock.
    playSfx('board-flip')
    setBoardView((v) => (v === 'letters' ? 'chess' : 'letters'))
  }, [])

  const [submitError, setSubmitError] = useState<string | null>(null)
  // #7 — transient "why your move was rejected" hint (e.g. a blank piece can't
  // capture until empowered — the rule is intentional, so we explain it the
  // moment the player tries). Auto-clears; the timer is cleared on re-trigger +
  // unmount.
  const [moveHint, setMoveHint] = useState<string | null>(null)
  const moveHintTimerRef = useRef<number | null>(null)
  // SCORE-04 / ARCA-597 — on-lift word verdict flash, relocated off the board
  // (where the old ScorePill sat) to the staged-word chip area near Submit.
  // WordTraceLayer fires onWordTraceComplete on lift; we flash the optimistic
  // valid/invalid here. The monotonic key restarts the flash on a rapid
  // re-trace instead of stacking.
  const [wordVerdict, setWordVerdict] = useState<{
    key: number
    outcome: 'valid' | 'invalid'
  } | null>(null)
  const wordVerdictKeyRef = useRef(0)
  // JUICE-03 — token-fly volley trigger; bumped on each tokens-earned claim.
  const [tokenFly, setTokenFly] = useState<{ key: number; count: number }>({
    key: 0,
    count: 0,
  })
  const [showEmpower, setShowEmpower] = useState(false)

  // Phase 32 Plan 32-04 (RUNE-35) — Quill 3-mode picker flow.
  //   • showQuillPicker mounts QuillModePicker (bottom-sheet).
  //   • quillMode (non-null) mounts QuillTargetOverlay (Pixi target picker)
  //     after the user selects Mark or Claim from the picker. Read mode
  //     short-circuits both states — onQuillModeSelect dispatches setPhaseB
  //     immediately and calls onSubmit, never setting quillMode.
  const [showQuillPicker, setShowQuillPicker] = useState(false)
  const [quillMode, setQuillMode] = useState<'mark' | 'claim' | null>(null)

  // Phase 32 Plan 32-05 (RUNE-35) — Mulligan selector flow.
  //   • showMulliganSelector mounts MulliganSelector (full-screen DOM overlay).
  //   • onPowerupSelect branches on 'lexical_mulligan' to set this flag.
  //   • Confirm dispatches setPhaseB({mode:'lexical_mulligan', payload:{cell_indices}})
  //     then calls onSubmit; Cancel discards selection without server call.
  const [showMulliganSelector, setShowMulliganSelector] = useState(false)

  // Phase 4 Gap 2 (P4) — DOM target-picker flow for the cell/piece-targeted
  // power-ups (castle_keep / tongue_tie / knights_vow / the_vanishing). The
  // production tray previously committed an empty payload for these, which the
  // server rejects. onPowerupSelect branches on these names to mount the
  // PowerupTargetSelector overlay; its Confirm sets the correct phaseB payload
  // then calls onSubmitChess (mirroring the MulliganSelector flow).
  const [powerupTarget, setPowerupTarget] = useState<
    'castle_keep' | 'tongue_tie' | 'knights_vow' | 'the_vanishing' | null
  >(null)

  // POWERUP-06 — the just-committed power-up drives a brief activate overlay
  // (§10.3). Set at each real commit dispatch point (alongside the 'powerup'
  // SFX, post-INT-07); cleared when the overlay auto-dismisses.
  const [activatedPowerup, setActivatedPowerup] = useState<PowerUpName | null>(
    null,
  )
  const dismissPowerupActivate = useCallback(() => {
    setActivatedPowerup(null)
  }, [])

  // Phase 31 Plan 31-03 Task 3 — Anchor placement tutorial idempotency guard.
  // Server clears `tutorial_anchor_consumption_first_time` atomically with
  // the consumption side-effect (Phase 26 D-LOCK), but a cached PlayerView
  // snapshot replay (e.g. reconnect race, WS replay) could re-serve it. The
  // session ref latches on first mount so the modal can NEVER re-mount in
  // this React tree's lifetime (T-31-04). NO browser-storage fallback —
  // cross-device coherent.
  const hasShownAnchorTutorialThisSessionRef = useRef<boolean>(false)
  const [tutorialDismissed, setTutorialDismissed] = useState<boolean>(false)
  // Empower clarity (role-first flow). The empower interaction is a small
  // local state machine:
  //   'idle'   — not empowering
  //   'role'   — EmpowerModal open, picking a role (showEmpower===true)
  //   'arming' — role chosen; the board glows the caller's empowerable pieces
  //              and waits for a piece tap to commit (empowerArmingRole set).
  // The committed empower only enters $turnBuilder.phaseB when a piece is
  // tapped, so the EMPOWER segment stays selected during config via an
  // effectiveB override (see render), without a placeholder in the store.
  const [empowerStage, setEmpowerStage] = useState<EmpowerStage>('idle')
  const [empowerArmingRole, setEmpowerArmingRole] =
    useState<EmpowerableRole | null>(null)
  // ARCA-591 (re-empower) — the cell_idx of the own empowered piece whose
  // ExtendModal is open, or null. Set by tapping (PieceDragLayer same-cell tap)
  // / re-Enter-ing (MatchGrid keyboard) one of the caller's own empowered
  // pieces during the chess step; cleared on confirm/close. Distinct from the
  // empower stage-machine: extend targets an ALREADY-empowered piece (the role
  // is fixed), so there is no role-pick / arming step.
  const [extendTargetCell, setExtendTargetCell] = useState<number | null>(null)
  const [bounceKey, setBounceKey] = useState(0)

  // WR-05 fix (Phase 20 review) — META-06 level-up reveal wiring.
  //   • `preMatchProfile` captures level + inventory once at mount.
  //   • On terminal status we refetch and diff: if level rose, derive
  //     `unlockedCosmeticIds` from the inventory delta and set
  //     `levelUp`. The WinOverlay already renders the LevelUpModal when
  //     `levelUp != null` — the diff is what was missing.
  //   • A one-shot ref guards against re-fire (state already terminal,
  //     post-match fetch already done) — mirrors the
  //     `matchCompletedDispatched` ref pattern below.
  const [preMatchProfile, setPreMatchProfile] = useState<{
    level: number
    inventory: ReadonlyArray<string>
    xp: number
  } | null>(null)
  const [levelUp, setLevelUp] = useState<WinOverlayLevelUpData | null>(null)
  // #13 — Real per-match XP delta. Computed once on the first terminal
  // render from post.xp - preMatchProfile.xp. The WinOverlay rewards grid
  // was previously hardcoded to placeholder values; GDD §9.1 only commits
  // to XP, so coins/gems/words have been dropped.
  const [xpDelta, setXpDelta] = useState<number>(0)
  const postMatchLevelDiffFiredRef = useRef(false)

  // Plan 20-07 META-08 — sticker overlay state. Inbound emote frames push
  // a new ActiveSticker (with a unique key + arrivedAt timestamp); rapid-
  // fire override prunes any prior sticker from the SAME senderSlot so
  // only the most-recent sticker per slot is visible (CONTEXT Area 4 Q4).
  //
  // WR-06 fix (Phase 20 review): keys use a monotonic per-component
  // counter instead of `Math.random().toString(36).slice(2, 8)`. The
  // previous random suffix made keys non-deterministic across renders
  // (StrictMode double-render saw different keys for the same emote on
  // first vs second render, defeating React reconciliation) and
  // additionally risked a 1-in-~10^6 collision under bursty emote
  // traffic (CR-01-adjacent — the per-socket bypass amplified the risk;
  // CR-01's fix lowers it but the determinism issue is independent).
  const [activeStickers, setActiveStickers] = useState<ActiveSticker[]>([])
  const stickerIdRef = useRef(0)
  useEffect(() => {
    const unregister = onEmoteReceived(({ emoteId, senderSlot, sentAt }) => {
      setActiveStickers((prev) => {
        const pruned = prev.filter((s) => s.senderSlot !== senderSlot)
        stickerIdRef.current += 1
        return [
          ...pruned,
          {
            key: `${emoteId}-${sentAt}-${stickerIdRef.current}`,
            emoteId,
            senderSlot,
            arrivedAt: Date.now(),
          },
        ]
      })
    })
    return unregister
  }, [])
  const handleStickerExpire = useCallback((key: string) => {
    setActiveStickers((prev) => prev.filter((s) => s.key !== key))
  }, [])

  // Plan 17-05 / Phase 32 Plan 32-03 — DOM-overlay cinematic gates.
  //   • FlopReveal driven by `reveal === 't6'`  (T6→T7 transition)
  //   • RiverReveal driven by `reveal === 't11'` (T11→T12 transition)
  //   • QueenRises has its own boolean gate (empower-to-queen confirmation)
  //   • WinOverlay renders when matchStatus === 'won'
  // The legacy RevealCinematic route for T1 / T12 was deleted from disk
  // in Plan 32-03 — the FSM-driven Flop + River reveals absorb both lanes.
  const flopRevealActive = reveal === 't6'
  const riverRevealActive = reveal === 't11'

  // Phase 31 Plan 31-04 (RUNE-32) — last claimed word cells. Captured
  // synchronously inside onSubmit BEFORE the turn-builder clears on
  // TURN_APPLIED. AffinityChipRow consumes this prop to pulse caller-affinity
  // matched chips for 600ms after the server confirms the word.
  const [lastClaimedWordCells, setLastClaimedWordCells] = useState<
    ReadonlyArray<PlayerView['board'][number]>
  >([])

  // TURN_APPLIED reaction — clear the turn-builder + reset transient UI
  // when the server confirms the turn (current_turn_idx advances).
  //
  // Phase 32 Plan 32-02 (RUNE-34) — reveal trigger wiring is layered on
  // top of the existing TURN_APPLIED branch. Two distinct transitions fire
  // the reveal cinematic:
  //   • 6 → 7  → setReveal('t6')   (THE FLOP — 12 new cells added)
  //   • 11 → 12 → setReveal('t11') (THE RIVER — 12 more cells added)
  // Critical guard (Pitfall 2 — reconnect replay safety): the outer
  // `prevTurnIdx.current !== null` predicate already prevents firing on
  // the FIRST PlayerView snapshot after mount or reconnect. Even if the
  // server replays a cached snapshot where `current_turn_idx === 7`, the
  // ref is null on initial render, the branch is skipped, and the ref
  // assignment at the bottom seeds the ref WITHOUT firing setReveal.
  const prevTurnIdx = useRef<number | null>(null)
  useEffect(() => {
    const curIdx = playerView?.current_turn_idx ?? null
    if (
      curIdx !== null &&
      prevTurnIdx.current !== null &&
      curIdx !== prevTurnIdx.current
    ) {
      clearTurnBuilder()
      setSubmitError(null)
      setShowEmpower(false)
      // Reset the empower stage machine on turn handoff.
      setEmpowerStage('idle')
      setEmpowerArmingRole(null)
      // Reveal triggers (RESEARCH §"Pattern 1" — RUNE-34). The setReveal
      // routes to the FSM-driven FlopReveal ('t6') + RiverReveal ('t11').
      //
      // ARCA-154 / GH #94 — fire on THRESHOLD CROSSINGS (prev < N && cur >= N),
      // not strict equality (=== 7 / === 12). Under fast WS batching the client
      // can see current_turn_idx jump 6→8 (or further) in one snapshot, so a
      // strict `=== 7` test misses the flop and the cinematic never mounts —
      // which means the floor-drop epoch never pulses and the board stays at its
      // old aspect ratio (back-ranks off-screen). A crossing test mounts the
      // cinematic so the *animated* grow fires even when the index skips.
      //
      // Check the river threshold first: a single batch can cross both (6→12+),
      // and at that point the board is already at the river (60 cells), so
      // "THE RIVER" is the correct cinematic to show. growCinematicInFlightRef
      // is set synchronously here (before the grow-rows effect runs this commit)
      // so resolveGrowRows holds the breathe-open instead of snapping early.
      if (prevTurnIdx.current < 12 && curIdx >= 12) {
        growCinematicInFlightRef.current = true
        setReveal('t11')
      } else if (prevTurnIdx.current < 7 && curIdx >= 7) {
        growCinematicInFlightRef.current = true
        setReveal('t6')
      }
    }
    prevTurnIdx.current = curIdx
  }, [playerView?.current_turn_idx])

  // Default Phase A to word mode at the start of the caller's composing turn.
  // Word is the default action (GDD §5.3); the WORD/SKIP toggle shows word as
  // selected, so the drag-trace + keyboard-trace surfaces must be active to
  // match. Runs after the clearTurnBuilder() transition effect above (so it
  // re-seeds each new turn) and on mount; also re-fires when an Anchor
  // placement gate clears mid-turn (same turn_idx), since Phase A only opens
  // once placement completes. The shouldDefaultPhaseAToWord guard keeps it
  // from clobbering an in-progress word or a deliberate skip.
  useEffect(() => {
    if (playerView === null) return
    if (
      shouldDefaultPhaseAToWord(
        {
          yourSlot: playerView.your_slot,
          currentPlayerSlot: playerView.current_player_slot,
          requiresAnchorPlacement: playerView.requires_anchor_placement,
        },
        $turnBuilder.get().phaseA,
      )
    ) {
      setPhaseA({ kind: 'word', word: '', path: [] })
    }
  }, [playerView])

  // Phase 18 Plan 04 (POLISH-07) — turn-state announcement.
  // Fires `a11y.announce.your_turn` when control transitions TO this player
  // and `a11y.announce.opponent_moved` when control transitions AWAY. The
  // ref-tracked previous slot prevents announcing on initial mount; the
  // module-scope debouncer inside useAnnounce coalesces rapid WS updates.
  const prevActiveSlot = useRef<0 | 1 | null>(null)
  useEffect(() => {
    const curActive = playerView?.current_player_slot ?? null
    const mySlot = playerView?.your_slot ?? null
    if (
      curActive !== null &&
      mySlot !== null &&
      prevActiveSlot.current !== null
    ) {
      if (curActive !== prevActiveSlot.current) {
        const isMyTurn = curActive === mySlot
        announce(
          intl.formatMessage({
            id: isMyTurn
              ? 'a11y.announce.your_turn'
              : 'a11y.announce.opponent_moved',
          }),
          'polite',
        )
      }
    }
    prevActiveSlot.current = curActive
  }, [playerView?.current_player_slot, playerView?.your_slot, announce, intl])

  // INT-02 — capture / king-capture cue. Diff the opponent's piece census
  // between the previously-rendered board and the incoming one: a decrease
  // means MY move (this onSubmitChess) or the OPPONENT's move (WS broadcast)
  // captured a piece. This single effect covers BOTH turns and is idempotent
  // — the optimistic applyLocalView and the authoritative re-broadcast carry
  // the same board, so the second frame shows no census change and never
  // double-fires. king-capture (match-deciding) takes precedence over the
  // generic capture thunk. Mute-gated (audio policy); not RM-gated.
  const prevBoardRef = useRef<PlayerView['board'] | null>(null)
  useEffect(() => {
    const board = playerView?.board ?? null
    const yourSlot = playerView?.your_slot ?? null
    const prev = prevBoardRef.current
    prevBoardRef.current = board
    if (board === null || yourSlot === null || prev === null) return
    if (board === prev) return
    const oppSlot = yourSlot === 0 ? 1 : 0
    const oppCount = (b: PlayerView['board']): number =>
      b.reduce((n, c) => (c.piece_owner_slot === oppSlot ? n + 1 : n), 0)
    const oppKing = (b: PlayerView['board']): boolean =>
      b.some((c) => c.piece_owner_slot === oppSlot && c.piece_kind === 'king')
    const kingFell = oppKing(prev) && !oppKing(board)
    if (kingFell) {
      playSfx('king-capture')
      // INT-01 — dark-pulse at the fallen king's cell (the match-deciding
      // shockwave §14). The cell was the opponent's king in the prior board;
      // emit before the WinOverlay mounts. RM-gated at the emit site.
      const kingCell = prev.find(
        (c) => c.piece_owner_slot === oppSlot && c.piece_kind === 'king',
      )
      if (kingCell) emitParticleBurst('dark-pulse', [kingCell.cell_idx])
    } else if (oppCount(board) < oppCount(prev)) {
      playSfx('capture')
    }
    // INT-02 — anchor-consume: a cell that was an Anchor is now Hollow. Pairs
    // with the sprite's stone-fall burst (LetterCubeSprite) and the optimistic
    // anchor-place cue. Fires ONCE per frame even if several anchors fell, so
    // simultaneous consumptions never stack the deep-impact thunk.
    const prevByIdx = new Map(prev.map((c) => [c.cell_idx, c]))
    const anchorConsumed = board.some((c) => {
      const p = prevByIdx.get(c.cell_idx)
      return p?.is_anchor === true && p.is_hollow === false && c.is_hollow
    })
    if (anchorConsumed) playSfx('anchor-consume')
    // #6 — opponent-empower threat cue. A cell where an OPPONENT piece gained a
    // role (role_kind null→non-null) since the prior board means the opponent
    // just empowered. The projection carries role_kind for both owners, but the
    // only cue was fired from the local tap handler (onEmpowerPieceTap), so
    // opponent empowerment was silent — the player noticed only when the piece
    // suddenly moved long-range. Fire a distinct THREAT cue (ominous dark-pulse,
    // NOT the player's triumphant magic-bloom) + the empower organ. Idempotent
    // like the capture arm (optimistic + authoritative frames carry the same
    // board); own empowers are excluded by the oppSlot filter.
    const oppEmpoweredCells = board
      .filter((c) => {
        const p = prevByIdx.get(c.cell_idx)
        return (
          c.piece_owner_slot === oppSlot &&
          c.role_kind !== null &&
          p?.piece_owner_slot === oppSlot &&
          p.role_kind === null
        )
      })
      .map((c) => c.cell_idx)
    if (oppEmpoweredCells.length > 0) {
      playSfx('empower')
      emitParticleBurst('dark-pulse', oppEmpoweredCells)
    }
  }, [playerView?.board, playerView?.your_slot])

  // Terminal-status detection — fires the runegambit:match-completed event
  // and triggers WinOverlay render when matchStatus === 'won'.
  const TERMINAL_STATUSES = useRef<ReadonlySet<string>>(
    new Set([
      'completed',
      'won',
      'lost',
      'drawn',
      'abandoned',
      'timeout_resolved',
      'stalemate_resolved',
    ]),
  )
  const matchCompletedDispatched = useRef(false)
  // ARCA-144 — one-shot guard so the client `matchesPlayed` counter (lobby
  // "Matches played: N") increments exactly once per match, no matter how
  // many times the match-completed listener re-registers (it re-runs on every
  // `onboarding` change). Mirrors the per-instance lifecycle of
  // matchCompletedDispatched: one MatchScreen instance == one match.
  const matchesPlayedCounted = useRef(false)
  // GAP-114 (Phase 21 BLOCKER-02 fix) — split from matchCompletedDispatched
  // so the SR announcement fires once-per-terminal-entry independent of
  // whether the matchMode lookup has resolved.
  const announcedTerminalRef = useRef(false)
  const isTerminal =
    matchStatus !== null && TERMINAL_STATUSES.current.has(matchStatus)
  // CINE-1 Task B — single source of truth for the end-of-match outcome.
  // Used by BOTH the WinOverlay visual and the SR announce block so the
  // heading text and the screen-reader announcement can never disagree.
  const terminalOutcome = useMemo(
    () => deriveTerminalOutcome(matchStatus, playerView),
    [matchStatus, playerView],
  )
  useEffect(() => {
    if (!isTerminal) return
    // GAP-114 fix (Phase 21 milestone audit BLOCKER-02) — closes the prior
    // TODO(WR-01). `matchMode` is resolved at mount via $lobbyData /
    // /api/home lookup against `ActiveMatchPreview.mode` (canonical source
    // — backed by the matches table). The match-completed dispatch is
    // GATED on matchMode having resolved: if isTerminal fires before the
    // /api/home roundtrip lands, we wait — the effect re-runs when
    // matchMode transitions from null → resolved, and the one-shot ref
    // guards against double-dispatch. If matchMode resolution genuinely
    // fails (network down, /api/home empty, etc.), the dispatch simply
    // does NOT fire and end-of-match modals stay closed for this match —
    // strictly safer than the previous "hardcode 'bot'" behavior which
    // over-triggered PushOptInModal for every match type and suppressed
    // FriendInviteModal entirely.
    if (matchMode !== null && !matchCompletedDispatched.current) {
      matchCompletedDispatched.current = true
      window.dispatchEvent(
        new CustomEvent('runegambit:match-completed', {
          detail: { mode: matchMode },
        }),
      )
    }

    // Phase 18 Plan 04 (POLISH-07) — terminal-state announcement via the
    // global polite <LiveRegion>. Replaces the inline `aria-live="polite"`
    // formerly attached to the end-of-match banner.
    //
    // CINE-1 Task B: keyed off `terminalOutcome` — the SAME derived value
    // the WinOverlay receives — so the SR text and the heading never disagree.
    // 'win' is skipped because the WinOverlay dialog (aria-labelledby the WIN!
    // heading) announces itself on mount via focus management. Every other
    // outcome announces the matching localized title.
    if (!announcedTerminalRef.current) {
      announcedTerminalRef.current = true
      if (terminalOutcome !== 'win') {
        const announceText =
          terminalOutcome === 'lose'
            ? intl.formatMessage({ id: 'match.end.lose.title' })
            : terminalOutcome === 'draw'
              ? intl.formatMessage({ id: 'match.end.draw.title' })
              : intl.formatMessage({ id: 'match.end.complete.title' })
        announce(announceText, 'polite')
      }
    }
  }, [isTerminal, terminalOutcome, matchMode, announce, intl])

  // Fetch /api/profile/me on mount to seed server-cached onboarding counters.
  // WR-05 fix (Phase 20 review): also capture pre-match {level, inventory}
  // so the terminal-state useEffect below can diff against the post-match
  // fetch and surface a LevelUpModal when the player crossed a level
  // threshold this match.
  useEffect(() => {
    // ARCA-493: skip this authenticated-only prefetch for anon practice
    // guests. `/api/profile/me` requires the `runegambit-client` audience and
    // 401s the anon JWT; apiFetch turns that terminal 401 into a bounce to
    // /splash (recoverToSplashOnAuthLoss, ARCA-94), yanking the guest out of
    // the bot match they just started. The seeded data (server onboarding
    // flags + pre-match level/inventory for the post-match level-up diff) is
    // meaningless for a guest with no server account, so skipping it is
    // loss-free — and it leaves `preMatchProfile` null, which also short-
    // circuits the post-match `/api/profile/me` diff below for guests.
    if (isAnonPracticeSession()) return
    const controller = new AbortController()
    void (async () => {
      try {
        const profile = await apiFetch<{
          real_matches_played: number
          onboarding_flags: {
            friendInviteShown?: boolean
            pushOptInShown?: boolean
          }
          level: number
          inventory: ReadonlyArray<string>
          xp: number
        }>('/api/profile/me', 'GET', { signal: controller.signal })
        setRealMatchesPlayedCache(profile.real_matches_played)
        const cur = $onboarding.get()
        $onboarding.set({
          ...cur,
          friendInviteShown:
            profile.onboarding_flags.friendInviteShown ?? cur.friendInviteShown,
          pushOptInShown:
            profile.onboarding_flags.pushOptInShown ?? cur.pushOptInShown,
        })
        setPreMatchProfile({
          level: profile.level,
          inventory: profile.inventory,
          xp: profile.xp,
        })
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
      }
    })()
    return () => {
      controller.abort()
    }
  }, [])

  // GAP-114 (Phase 21 milestone audit BLOCKER-02) — resolve THIS match's
  // mode at mount so the runegambit:match-completed dispatch can route
  // correctly: the bot path (ARCA-147) now defers the push opt-in to the lobby
  // via navigateHome, while the FriendInviteModal (casual/ranked/friend) still
  // fires from the match-completed listener. Source of truth:
  //   1. $lobbyData.active_matches (synchronous — populated when the user
  //      arrived from the lobby; OpenAPI `ActiveMatchPreview.mode` field).
  //   2. /api/home fetch fallback (when the user deep-linked into the
  //      match, or hit it before lobby polling first ran). `/api/home`
  //      returns `mode='tournament_round'` rows in `active_matches` too
  //      (per the OpenAPI description), covering the TournamentCard nav
  //      path.
  // If neither resolves (race: match completes before /api/home returns,
  // or user resumed a match no longer in the active list), `matchMode`
  // stays null and the match-completed dispatch is skipped — strictly
  // safer than the prior hardcoded 'bot' which mis-routed every match.
  useEffect(() => {
    // Synchronous lookup first — no network if the lobby store already
    // has this match.
    const cached = $lobbyData.get()
    const cachedRow = cached?.active_matches.find((m) => m.match_id === matchId)
    if (cachedRow && isMatchMode(cachedRow.mode)) {
      setMatchMode(cachedRow.mode)
      return
    }
    // ARCA-493: skip the best-effort /api/home prefetch for anon practice
    // guests — same authenticated-only-audience 401 → /splash bounce as the
    // profile prefetch above. Tournament match-mode resolution never applies
    // to an anon bot-practice match, so the skip is loss-free: `matchMode`
    // simply stays null, exactly as it does on a fetch failure. (A guest who
    // arrived from the lobby with cached match data is still resolved
    // synchronously above — only the network fallback is skipped.)
    if (isAnonPracticeSession()) return
    // Fallback — fetch /api/home and look up the match. Best-effort: a
    // failure leaves matchMode null (dispatch suppressed). `fetchHome()`
    // does not currently expose an AbortSignal, so unmount-safety is
    // handled via a local `cancelled` flag rather than an AbortController.
    let cancelled = false
    void (async () => {
      try {
        const home = await fetchHome()
        if (cancelled) return
        const row = home.active_matches.find((m) => m.match_id === matchId)
        if (row && isMatchMode(row.mode)) {
          setMatchMode(row.mode)
        }
      } catch {
        // Best-effort — match-completed dispatch will be skipped this
        // match, which is the safer default.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [matchId])

  // WR-05 fix (Phase 20 review) — META-06 post-match level-up diff. Fires
  // once on the first terminal-status render. If the level rose, derive
  // `unlockedCosmeticIds` from the inventory delta and feed it to
  // WinOverlay. Best-effort — a fetch failure leaves levelUp at null
  // (the WinOverlay still renders without the modal).
  useEffect(() => {
    if (!isTerminal) return
    if (postMatchLevelDiffFiredRef.current) return
    if (preMatchProfile === null) return
    postMatchLevelDiffFiredRef.current = true
    const controller = new AbortController()
    void (async () => {
      try {
        const post = await apiFetch<{
          level: number
          inventory: ReadonlyArray<string>
          xp: number
        }>('/api/profile/me', 'GET', { signal: controller.signal })
        // #13 — real XP delta drives the WinOverlay rewards grid. Clamp at
        // 0: bot_loss awards 0 XP (DEC-011), so the delta should never go
        // negative, but defensive against a server-side reset.
        const delta = Math.max(0, post.xp - preMatchProfile.xp)
        setXpDelta(delta)
        if (post.level > preMatchProfile.level) {
          const prevSet = new Set(preMatchProfile.inventory)
          const newCosmetics = post.inventory.filter((id) => !prevSet.has(id))
          setLevelUp({
            newLevel: post.level,
            unlockedCosmeticIds: newCosmetics,
          })
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return
        // Best-effort — log only; LevelUpModal stays closed on fetch fail.
        console.warn('[MatchScreen] post-match profile/me diff failed', err)
      }
    })()
    return () => {
      controller.abort()
    }
    // We intentionally depend ONLY on isTerminal — preMatchProfile is read
    // through the ref-guard pattern and the ref ensures the body runs at
    // most once per MatchScreen instance.
  }, [isTerminal, preMatchProfile])

  // runegambit:match-completed listener — fires end-of-match modals.
  //
  // ARCA-147 — the bot-match PushOptInModal is intentionally NOT triggered
  // here. Firing it synchronously on match-completed rendered the modal over
  // the terminal WIN/LOSE/DRAW overlay (obscuring the result) and the "your
  // turn" framing is wrong for an instantly-resolved bot match. The opt-in is
  // now deferred to the lobby: `navigateHome` sets a one-shot sessionStorage
  // baton on HOME navigation out of a completed bot match, and LobbyScreen
  // surfaces the prompt on its next mount. Only the real-match FriendInviteModal
  // path remains driven from this listener.
  useEffect(() => {
    function onMatchCompleted(ev: Event): void {
      const detail = (ev as CustomEvent<MatchCompletedDetail>).detail
      if (!detail) return
      // ARCA-144 — bump the client session counter shown in the lobby footer
      // ("Matches played: N") for EVERY completed match, including bot
      // practice. The ref makes it exactly-once per match: the dispatcher
      // already fires runegambit:match-completed at most once per MatchScreen
      // instance, and this guard is belt-and-braces against the listener
      // re-registering (its effect re-runs on each `onboarding` change).
      if (!matchesPlayedCounted.current) {
        matchesPlayedCounted.current = true
        incrementMatchesPlayed()
      }
      const isReal =
        detail.mode === 'casual' ||
        detail.mode === 'ranked' ||
        detail.mode === 'friend'
      if (
        isReal &&
        !onboarding.friendInviteShown &&
        onboarding.realMatchesPlayed + 1 === 3
      ) {
        setShowFriendInvite(true)
      }
    }
    window.addEventListener(
      'runegambit:match-completed',
      onMatchCompleted as EventListener,
    )
    return () => {
      window.removeEventListener(
        'runegambit:match-completed',
        onMatchCompleted as EventListener,
      )
    }
  }, [onboarding])

  // Wire the runegambit:place-anchor CustomEvent from MatchGrid. Empower and
  // the power-up tray no longer use window events — both are driven directly
  // from the Phase B selection handler (beginEmpower / setShowPowerupTray) and,
  // for empower, the armed-board tap (onEmpowerPieceTap).
  useEffect(() => {
    // Phase 31 — DOM MatchGrid cell taps during the T2/T3 anchor-placement
    // gate dispatch this event (the Pixi AnchorPlacementOverlay sits beneath
    // the DOM grid in stacking order so its own onPointerDown never fires).
    // MatchScreen owns the network call so the failure flow stays unified
    // with the existing setSubmitError + assertive announce pattern
    // (handleAnchorPlaceFailure is the same shape but declared later in the
    // file, so we inline it here to avoid a TDZ reference in the deps).
    function onPlaceAnchorRequest(e: Event): void {
      const detail = (e as CustomEvent<{ cell_idx: number }>).detail
      if (!detail || typeof detail.cell_idx !== 'number') return
      // §10.11 anchor placement — warm bell + stone-set (the cube emits the
      // stone-carve particle burst in parallel). Optimistic on the request.
      playSfx('anchor-place')
      void placeAnchor(matchId, { cell_idx: detail.cell_idx }).catch(
        (error: unknown) => {
          // ARCA-599 — surface the specific reason (e.g. cell_has_piece → "that
          // rune already holds a piece") instead of the generic dead-end.
          const { bannerId, announceId } = anchorFailureMessageIds(error)
          setSubmitError(intl.formatMessage({ id: bannerId }))
          announce(intl.formatMessage({ id: announceId }), 'assertive')
        },
      )
    }
    window.addEventListener('runegambit:place-anchor', onPlaceAnchorRequest)
    return () => {
      window.removeEventListener(
        'runegambit:place-anchor',
        onPlaceAnchorRequest,
      )
    }
  }, [matchId, intl, announce])

  // WordTraceLayer onComplete — assemble word string from path + commit.
  // Plan 30-04 refresh: Runespring unifies the field — every cell with a
  // non-null `letter` participates in word tracing (Hollows excluded by
  // letter===null).
  const onWordTraceComplete = useCallback(
    (path: number[]) => {
      if (playerView === null) return
      const letterByCell = new Map<number, string>()
      for (const cell of playerView.board) {
        if (cell.letter !== null && !cell.is_hollow) {
          letterByCell.set(cell.cell_idx, cell.letter)
        }
      }
      const word = path.map((i) => letterByCell.get(i) ?? '').join('')
      setPhaseA({ kind: 'word', word, path })
      // SCORE-04 / ARCA-597 — flash the on-lift verdict beside the staged chip
      // (relocated off the board). WordTraceLayer only calls this for a real
      // trace (path length >= 2). Optimistic: valid iff the trace is long
      // enough to score (honest client signal; SCORE-01). A server rejection
      // upgrades it to invalid via the runegambit:word-verdict event the
      // ScorePillVerdict listens for.
      const minLen = playerView.config?.minWordLength ?? 3
      wordVerdictKeyRef.current += 1
      setWordVerdict({
        key: wordVerdictKeyRef.current,
        outcome: path.length >= minLen ? 'valid' : 'invalid',
      })
    },
    [playerView],
  )

  const onPieceDragComplete = useCallback((from: number, to: number) => {
    stageMove({ from, to })
  }, [])

  // #7 — the player tried to capture an adjacent enemy with an un-empowered
  // "blank" piece. That capture is intentionally illegal (blanks step one
  // empty cell; capturing needs a role), so instead of a silent snap-back we
  // explain why + nudge toward EMPOWER. Soft error tick + a transient,
  // auto-clearing coach hint (announced for screen readers).
  const onBlankCaptureBlocked = useCallback(() => {
    playSfx('ui-error')
    const msg = intl.formatMessage({
      id: 'match.move.blank_cannot_capture',
      defaultMessage: 'Empower this piece first — it can’t capture yet.',
    })
    announce(msg, 'polite')
    if (moveHintTimerRef.current !== null) {
      window.clearTimeout(moveHintTimerRef.current)
    }
    setMoveHint(msg)
    moveHintTimerRef.current = window.setTimeout(() => {
      setMoveHint(null)
      moveHintTimerRef.current = null
    }, 3500)
  }, [intl, announce])

  // #7 — drop the auto-clear timer on unmount.
  useEffect(() => {
    return () => {
      if (moveHintTimerRef.current !== null) {
        window.clearTimeout(moveHintTimerRef.current)
      }
    }
  }, [])

  // ARCA-145 (Bug B) — auto-dismiss the submit-error banner. The banner renders
  // with `pointer-events: none` so it can never swallow a tap meant for the
  // Submit button beneath it (the old soft-lock), which means it no longer
  // relies on the next Submit press to clear. A short timer guarantees a stale
  // error never lingers over the control deck. Re-arms on each new error and is
  // cleared on change/unmount.
  useEffect(() => {
    if (submitError === null) return
    const timer = window.setTimeout(() => {
      setSubmitError(null)
    }, SUBMIT_ERROR_AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [submitError])

  // ── Empower (role-first flow) ───────────────────────────────────────────
  // The committed Phase-A word length the server's validateEmpower checks
  // against (source_word_length). Chess step: the caller-only projection (the
  // in-memory phaseA was reset on word-commit). Word step: the live trace.
  const committedWordLength = useCallback((): number => {
    if (playerView?.turn_phase === 'chess') {
      return playerView.your_pending_word_length ?? 0
    }
    const phaseA = $turnBuilder.get().phaseA
    return phaseA?.kind === 'word' ? phaseA.word.length : 0
  }, [playerView])

  // EMPOWER tapped — open the role modal, but only when empower is actually
  // playable. Guards eliminate the prior silent dead-ends: empower needs the
  // caller's own non-king piece on the board AND a word this turn (a skip turn
  // cannot empower — validateEmpower requires source_word_length). Failures
  // surface on the visible submitError banner + an assertive announce, never a
  // silent no-op.
  const beginEmpower = useCallback(() => {
    if (playerView === null) return
    const hasPiece = playerView.board.some(
      (c) =>
        c.piece_owner_slot === playerView.your_slot && c.piece_kind === 'blank',
    )
    const guard = (id: string): void => {
      const msg = intl.formatMessage({ id })
      setSubmitError(msg)
      announce(msg, 'assertive')
    }
    if (!hasPiece) {
      guard('match.empower.guard.no_piece')
      return
    }
    if (committedWordLength() < 1) {
      guard('match.empower.guard.no_word')
      return
    }
    setSubmitError(null)
    setEmpowerArmingRole(null)
    setEmpowerStage('role')
    setShowEmpower(true)
  }, [playerView, intl, announce, committedWordLength])

  // EmpowerModal confirm — a role was chosen. ARM the board for piece-select
  // (does NOT commit; the piece tap does). Keeps the chosen role so re-opening
  // pre-selects it.
  const onEmpowerConfirm = useCallback((role: EmpowerableRole) => {
    setEmpowerArmingRole(role)
    setEmpowerStage('arming')
    setShowEmpower(false)
  }, [])

  // A glowing piece was tapped while armed — stage the empower into the phaseB
  // composite (optimistic role glyph via chessViewPlayerView) and fire the
  // bloom / QueenRises cinematic at the chosen cell. Staging clears any staged
  // extend (empower XOR extend — D2), handled by stageEmpower.
  const onEmpowerPieceTap = useCallback(
    (cellIdx: number) => {
      if (empowerArmingRole === null || playerView === null) return
      const role = empowerArmingRole
      stageEmpower({
        cell_idx: cellIdx,
        role,
        source_word_length: committedWordLength(),
      })
      setEmpowerStage('idle')
      setEmpowerArmingRole(null)
      // INT-01 — magic-bloom at the empowered piece's cell. Queen gets a heavier
      // bloom layered under the QueenRises cinematic; other roles the canon
      // count. RM-gated at the emit site.
      emitParticleBurst(
        'magic-bloom',
        [cellIdx],
        role === 'queen' ? 18 : undefined,
      )
      if (role === 'queen') setQueenRisesActive(true)
    },
    [empowerArmingRole, playerView, committedWordLength],
  )

  // Cancel an in-progress empower (banner Cancel / Escape / switching action).
  const cancelEmpower = useCallback(() => {
    setEmpowerStage('idle')
    setEmpowerArmingRole(null)
    setShowEmpower(false)
  }, [])

  // ARCA-619 / ARCA-715 — per-slot clear handlers for the composite chess step.
  // Replaces the old "discard staged action?" confirm dialog: each staged slot
  // is cleared explicitly and independently (the other slots are untouched), so
  // there is nothing destructive to confirm. Clearing the role slot also
  // cancels any in-progress empower config (role modal / arming).
  const onClearStagedMove = useCallback(() => stageMove(null), [])
  const onClearStagedRole = useCallback(() => {
    cancelEmpower()
    stageEmpower(null)
    stageExtend(null)
  }, [cancelEmpower])
  const onClearStagedPowerup = useCallback(() => stagePowerup(null), [])

  const onPowerupSelect = useCallback(
    (
      name:
        | 'scriveners_quill'
        | 'tongue_tie'
        | 'lexical_mulligan'
        | 'knights_vow'
        | 'castle_keep'
        | 'anagram_sigil'
        | 'the_vanishing'
        | 'whisperers_promise',
    ) => {
      // §10.3/§12.4 power-up activation — the magical whoosh fires at the REAL
      // dispatch point, NOT here (INT-07). scriveners_quill / lexical_mulligan
      // / cell-targeted variants only OPEN a picker the user may cancel, so a
      // cue here would play the activation sound for a power-up that never
      // commits. Each branch below that actually dispatches (or its
      // picker/selector callback) plays 'powerup' itself.
      // INT-03: magic-bloom IS emitted for the Quill target (onQuillTargetTap
      // below) and for empower; the other power-ups (cell-targeted / no-target /
      // Mulligan) play only their audio cue today — no companion particle yet.
      // Phase 32 Plan 32-04 (RUNE-35) — Quill activation opens the 3-mode
      // picker. The staging happens AFTER mode + (optional) target selection
      // in onQuillModeSelect / onQuillTargetTap below; do NOT stage here
      // for scriveners_quill (the picker owns the staging).
      if (name === 'scriveners_quill') {
        setShowQuillPicker(true)
        return
      }
      // Phase 32 Plan 32-05 (RUNE-35) — Lexical Mulligan opens the 6-cell
      // selector overlay. The staging happens AFTER cell selection inside
      // the MulliganSelector's onSubmit callback; do NOT stage here
      // (the selector owns the staging).
      if (name === 'lexical_mulligan') {
        setShowMulliganSelector(true)
        return
      }
      // Phase 4 Gap 2 (P4) — cell/piece-targeted power-ups open the DOM
      // PowerupTargetSelector. The staging happens AFTER the target pick
      // inside the overlay's onConfirm callback; do NOT stage here.
      if (
        name === 'castle_keep' ||
        name === 'tongue_tie' ||
        name === 'knights_vow' ||
        name === 'the_vanishing'
      ) {
        setPowerupTarget(name)
        return
      }
      // No-target power-ups (anagram_sigil / whisperers_promise) STAGE
      // immediately — empty payload is correct (cross-checked against the
      // backend effect files; anagram-sigil.ts emits payload:{} with NO target
      // validation). INT-07: staging IS the activation, so the whoosh fires
      // here; the composite is committed by the single Submit.
      playSfx('powerup')
      setActivatedPowerup(name)
      stagePowerup({ name, payload: {} })
    },
    [],
  )

  // Phase 32 Plan 32-04 (RUNE-35) — Quill picker dismissal handler. Used by
  // both Cancel button and tap-outside / Escape paths. Clears both showQuill-
  // Picker (the bottom-sheet) and quillMode (the target-overlay) so the
  // Quill flow returns to the PowerupTray default with no half-state.
  const onQuillDismiss = useCallback(() => {
    setShowQuillPicker(false)
    setQuillMode(null)
  }, [])

  // onQuillTargetTap (Phase 32 Plan 32-04 RUNE-35) is declared AFTER onSubmit
  // below so it can `void onSubmit()` directly.

  // Plan 3 Task 4 (two-step turn) — Phase A (word) commit. Active when
  // playerView.turn_phase === 'word' and it's the local player's turn. Reads
  // $turnBuilder.phaseA, applies the word guards (non-null; trace length ≥ 2),
  // POSTs /turn/word, then applies the step response's your_state directly so
  // the UI flips to the chess step WITHOUT waiting for the WS round-trip.
  const onSubmitWord = useCallback(async () => {
    const latestTurn = $turnBuilder.get()
    if (playerView === null) return
    // Guide the user when no Phase A intent has been chosen — bounce the
    // word-trace controls.
    if (latestTurn.phaseA === null) {
      // No setBoardView here — during the word step effectiveBoardView is
      // already driven to 'letters' by turn_phase (feat/two-step-turn).
      setBounceKey((k) => k + 1)
      return
    }
    if (
      latestTurn.phaseA.kind === 'word' &&
      latestTurn.phaseA.path.length < 2
    ) {
      setSubmitError(intl.formatMessage({ id: 'match.error.invalid_trace' }))
      announce(
        intl.formatMessage({ id: 'a11y.announce.submit_failure' }),
        'assertive',
      )
      return
    }
    try {
      setSubmitError(null)
      // Phase 31 Plan 31-04 (RUNE-32) — snapshot the just-claimed cells (word
      // commits only) so AffinityChipRow can pulse matching chips for 600ms.
      // JUICE-1: build the snapshot here but PUBLISH it only AFTER the server
      // accepts the word. The previous code called setLastClaimedWordCells
      // pre-await; React batched it, so AffinityChipRow pulsed chips, played
      // 'affinity-ping', and fired a polite SR announce for words the server
      // then REJECTED — celebrating a word that was never claimed.
      const claimedCells: PlayerView['board'][number][] = []
      if (latestTurn.phaseA.kind === 'word') {
        const path = latestTurn.phaseA.path
        const cellByIdx = new Map(playerView.board.map((c) => [c.cell_idx, c]))
        for (const idx of path) {
          const c = cellByIdx.get(idx)
          if (c) claimedCells.push(c)
        }
      }
      const resp = await submitWord({
        turnIdx: playerView.current_turn_idx,
        player_slot: playerView.your_slot,
        phase_a: latestTurn.phaseA,
      })
      // Apply the step response directly so the UI flips to the chess step
      // immediately (the authoritative WS broadcast that follows is idempotent).
      applyLocalView(resp.your_state)
      // Surface word feedback through the polite live region + the signature
      // word-claim SFX (§9.7 / AUDIO-05): a bright bell on a valid word, a
      // golden coin clink when tokens are earned, a soft buzz on rejection.
      if (resp.phase_a_word_rejected) {
        playSfx('word-invalid')
        // SCORE-04 — upgrade the optimistic on-lift verdict to invalid (the
        // dictionary rejection only the server knows about).
        window.dispatchEvent(
          new CustomEvent('runegambit:word-verdict', {
            detail: { outcome: 'invalid' },
          }),
        )
        // JUICE-1: clear any prior pulse — this word was not claimed.
        setLastClaimedWordCells([])
        announce(
          intl.formatMessage({ id: 'match.turn.word_rejected' }),
          'polite',
        )
      } else {
        // JUICE-1: publish the affinity pulse only on an ACCEPTED word.
        if (latestTurn.phaseA.kind === 'word') {
          setLastClaimedWordCells(claimedCells)
        }
        playSfx('word-valid')
        // SCORE-04 — confirm the on-lift verdict (no-op if it was already valid;
        // dispatched for symmetry/future-proofing).
        window.dispatchEvent(
          new CustomEvent('runegambit:word-verdict', {
            detail: { outcome: 'valid' },
          }),
        )
        if (resp.tokens_earned > 0) {
          playSfx('token-clink', { intensity: 1 })
          // INT-01 — coin-spark burst across the just-claimed word's cells (the
          // board-space companion to the DOM token-fly volley + token-clink).
          // RM-gated at the emit site (EffectLayer). claimedCells is the word
          // that actually scored (set only on the accepted branch — JUICE-1).
          emitParticleBurst(
            'coin-spark',
            claimedCells.map((c) => c.cell_idx),
          )
          // JUICE-03 — launch the token-fly coin volley to the bank counter.
          setTokenFly((p) => ({ key: p.key + 1, count: resp.tokens_earned }))
          announce(
            intl.formatMessage(
              { id: 'match.turn.word_scored' },
              { tokens: resp.tokens_earned },
            ),
            'polite',
          )
        }
      }
      clearPhaseA()
    } catch (err) {
      if (err instanceof AuthInvalidError) return
      // ARCA-145 (Bug A) — clear the stale Phase A trace so MatchGrid drops
      // `data-cell-in-trace` and the player can re-trace immediately (mirror the
      // success path). Without this a 4xx/409 soft-locks the board: the failed
      // word's cells stay highlighted and a fresh submit never fires.
      clearPhaseA()
      // ARCA-145 (Bug D) — surface the actionable rejection reason the 409 body
      // carries (e.g. `cell_frozen_by_tongue_tie`), falling back to the neutral
      // generic copy for unmapped reasons / non-ApiError failures.
      setSubmitError(intl.formatMessage({ id: submitErrorMessageId(err) }))
      announce(
        intl.formatMessage({ id: 'a11y.announce.submit_failure' }),
        'assertive',
      )
    }
  }, [playerView, submitWord, applyLocalView, intl, announce])

  // Plan 3 Task 4 / ARCA-619 — Phase B (chess) commit. Active when
  // turn_phase === 'chess' and it's the local player's turn. Projects the
  // $turnBuilder.phaseB COMPOSITE into the CompositeChessStep wire body
  // (toChessStepBody), POSTs /turn/chess, applies the step response's
  // your_state, then clears the full turn builder. The move / empower / extend /
  // powerup / mulligan / quill sub-flows all STAGE into the composite; THIS is
  // the single commit. An empty composite is the valid do-nothing step (replaces
  // Pass — D4), so there is no "nothing selected" bounce.
  const onSubmitChess = useCallback(async () => {
    const latestTurn = $turnBuilder.get()
    if (playerView === null) return
    try {
      setSubmitError(null)
      const resp = await submitChess({
        turnIdx: playerView.current_turn_idx,
        player_slot: playerView.your_slot,
        phase_b: toChessStepBody(latestTurn.phaseB),
      })
      applyLocalView(resp.your_state)
      clearTurnBuilder()
    } catch (err) {
      if (err instanceof AuthInvalidError) return
      setSubmitError(intl.formatMessage({ id: 'match.error.submit_failed' }))
      announce(
        intl.formatMessage({ id: 'a11y.announce.submit_failure' }),
        'assertive',
      )
    }
  }, [playerView, submitChess, applyLocalView, intl, announce])

  // ARCA-591 (re-empower) — a board piece was tapped/re-Entered for inspection.
  // Opens the ExtendModal ONLY for the caller's OWN already-empowered piece
  // during the chess step (extend is a Phase-B action; role data is per-cell).
  // Any other tap (opponent piece, blank/king, word step, opponent's turn) is a
  // no-op so movement-select / empower-arming are never disturbed — the
  // PieceDragLayer same-cell tap and the MatchGrid keyboard path both route
  // here and the guard below is the single precedence decision. King carries
  // `role_kind === null` server-side, so the `!== 'king'` check is defensive.
  const onInspectPieceTap = useCallback(
    (cellIdx: number) => {
      if (playerView === null) return
      if (playerView.current_player_slot !== playerView.your_slot) return
      if (playerView.turn_phase !== 'chess') return
      const cell = playerView.board.find((c) => c.cell_idx === cellIdx)
      if (
        cell === undefined ||
        cell.piece_owner_slot !== playerView.your_slot ||
        cell.role_kind === null ||
        cell.role_kind === 'king'
      ) {
        return
      }
      setExtendTargetCell(cellIdx)
    },
    [playerView],
  )

  const onExtendClose = useCallback(() => setExtendTargetCell(null), [])

  // Confirm re-empower (ARCA-619): STAGE the `extend` slot for the target cell
  // (empower XOR extend — stageExtend clears any staged empower). No auto-submit
  // — the staged-chess summary shows the re-empower chip and the player commits
  // it (composed with any move / powerup) via the single Submit.
  const onExtendConfirm = useCallback(() => {
    const cellIdx = extendTargetCell
    if (cellIdx === null) return
    setExtendTargetCell(null)
    stageExtend({ cell_idx: cellIdx })
  }, [extendTargetCell])

  // Phase 32 Plan 32-04 (RUNE-35) — Quill mode-select handler. Mark/Claim routes
  // to QuillTargetOverlay via setQuillMode; Read STAGES the powerup slot
  // immediately (self-target — no target_cell_idx). ARCA-619: staging only — the
  // composite is committed by the single Submit.
  const onQuillModeSelect = useCallback((mode: QuillMode) => {
    setShowQuillPicker(false)
    if (mode === 'read') {
      // INT-07: Read mode stages immediately — fire the activation whoosh here
      // (Mark/Claim defer to onQuillTargetTap below).
      playSfx('powerup')
      setActivatedPowerup('scriveners_quill')
      stagePowerup({
        name: 'scriveners_quill',
        payload: { mode: 'read' },
      })
      return
    }
    setQuillMode(mode)
  }, [])

  // Phase 32 Plan 32-04 (RUNE-35) — Quill target-overlay tap handler. Read
  // mode never reaches this path (it short-circuits in onQuillModeSelect);
  // only Mark + Claim flow here. STAGES the powerup slot with the full payload
  // including target_cell_idx and clears quillMode (ARCA-619: no auto-submit).
  const onQuillTargetTap = useCallback(
    (cellIdx: number) => {
      if (quillMode === null) return
      // INT-07: Mark/Claim stage on the target tap — fire the whoosh here.
      playSfx('powerup')
      setActivatedPowerup('scriveners_quill')
      // INT-01: magic-bloom at the Quill target cell (RM-gated at emit site).
      emitParticleBurst('magic-bloom', [cellIdx])
      stagePowerup({
        name: 'scriveners_quill',
        payload: { mode: quillMode, target_cell_idx: cellIdx },
      })
      setQuillMode(null)
    },
    [quillMode],
  )

  const tokens = playerView?.your_tokens ?? 0
  // Plan 3 Task 4 (two-step turn) — the active phase is driven by the
  // server-projected turn_phase. 'word' → Phase A (word commit); 'chess' →
  // Phase B (move / empower / power / pass). Defaults to 'word' pre-snapshot.
  const turnPhase: 'word' | 'chess' = playerView?.turn_phase ?? 'word'
  // Phase 31 Plan 31-03 Task 3 — Submit/Skip blocked while Phase A Anchor-
  // placement gate is unresolved. Server sets requires_anchor_placement at
  // T2/T3/T8/T9 (Phase 26 RUNE-15) and clears it on successful POST. The
  // overlay (Task 1) drives the placement flow; here we only gate Submit.
  // ARCA-769 (ARCA-717 R2) — composite-turn empower/move conflict guard. The
  // canonical apply order is powerup → empower → move (backend D3), so a move of
  // a piece empowered THIS turn is validated against the NEW role and 409s
  // `phase_b:illegal_move` if it no longer fits. `your_legal_moves` reflects the
  // pre-empower board, so recompute legality under the staged role client-side
  // (chess-move-geometry mirror) to warn + block Submit before it round-trips.
  const moveEmpowerConflict = stagedMoveEmpowerConflict(playerView, turn.phaseB)
  const submitDisabled =
    playerView === null ||
    status === 'error' ||
    playerView.requires_anchor_placement === true ||
    // Not the local player's turn → no submitting (defense-in-depth; the whole
    // action-control region is also hidden when it's the opponent's turn).
    playerView.current_player_slot !== playerView.your_slot ||
    // Same-piece empower+move where the move is illegal under the new role.
    moveEmpowerConflict
  // Single Submit affordance dispatches to the active phase's submitter.
  const onSubmitActivePhase = useCallback(() => {
    if (turnPhase === 'word') void onSubmitWord()
    else void onSubmitChess()
  }, [turnPhase, onSubmitWord, onSubmitChess])

  // Phase 31 Plan 31-03 Task 3 — tutorial mount-gate derivation. Once-per-
  // session: server flag AND not yet dismissed AND ref not latched.
  const shouldShowAnchorTutorial =
    playerView !== null &&
    playerView.tutorial_anchor_consumption_first_time === true &&
    !tutorialDismissed &&
    !hasShownAnchorTutorialThisSessionRef.current

  // Surface place-anchor POST failures via the existing submitError banner +
  // assertive announce flow. ARCA-599 — map the STATE_CONFLICT reason to the
  // most specific copy (cell_has_piece → occupied-cell message) on both the
  // visual banner and the assertive SR announce, falling back to the generic
  // copy for unmapped reasons.
  const handleAnchorPlaceFailure = useCallback(
    (error: unknown) => {
      const { bannerId, announceId } = anchorFailureMessageIds(error)
      setSubmitError(intl.formatMessage({ id: bannerId }))
      announce(intl.formatMessage({ id: announceId }), 'assertive')
    },
    [intl, announce],
  )

  // ANCHOR-08 (ARCA-143 / ARCA-120) — transient "Anchor placed!" confirmation.
  // On a successful placement the overlay's only feedback is a 4px optimistic
  // dot before the next server snapshot clears the gate and unmounts everything.
  // We latch a flag for ANCHOR_PLACED_CONFIRM_MS so AnchorPlacementPrompt can
  // flip its copy to "Anchor placed!" (and stay mounted) for a clearly-visible
  // beat even when the snapshot arrives near-instantly (e.g. bot matches).
  const [anchorJustPlaced, setAnchorJustPlaced] = useState(false)
  const anchorPlacedTimerRef = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (anchorPlacedTimerRef.current !== null) {
        window.clearTimeout(anchorPlacedTimerRef.current)
      }
    },
    [],
  )
  const handleAnchorPlaceSuccess = useCallback(() => {
    setAnchorJustPlaced(true)
    if (anchorPlacedTimerRef.current !== null) {
      window.clearTimeout(anchorPlacedTimerRef.current)
    }
    anchorPlacedTimerRef.current = window.setTimeout(() => {
      setAnchorJustPlaced(false)
      anchorPlacedTimerRef.current = null
    }, ANCHOR_PLACED_CONFIRM_MS)
  }, [])

  // Phase 24 (RUNE-05) + Phase 31 (Plan 31-05 closure sweep): Phase A is
  // narrowed to {kind:'word'|'skip'} only (see stores/turnBuilder
  // PhaseASelection). The Runespring shared-field model has no rune-rack tile
  // row and no swap-mode mount gate — all retired letter-shuffle plumbing
  // (component + JSX mount + custom-event listener) was deleted; only this
  // header comment marks where the surface used to live.

  const dataNetStatus =
    status === 'ws' || status === 'polling' ? 'connected' : status

  // PhaseControls scalar adapter — read live from $turnBuilder so the selected
  // Phase A segment always reflects the current intent. (Phase B is no longer a
  // segment — it is the composite ChessControlDeck.)
  const phaseAValue: PhaseAValue | null = phaseAFromBuilder(turn.phaseA)

  // Plan 20-07 META-08 — derive isMyTurn for EmotePicker disabled gating.
  // The picker is available DURING opponent's turn only (CONTEXT Area 4 Q4).
  // Also disable on non-active match status so it can't fire post-completion.
  const mySlot: 0 | 1 = (playerView?.your_slot ?? 0) as 0 | 1
  const isMyTurn =
    playerView !== null &&
    playerView.current_player_slot === playerView.your_slot
  // Two-step turn (feat/two-step-turn) — during the player's OWN active turn
  // the rendered board view MUST follow turn_phase so the canvas matches the
  // active interaction overlay (word → letters trace; chess → piece drag):
  //   • turn_phase === 'word'  → 'letters'
  //   • turn_phase === 'chess' → 'chess'
  // When it is NOT your turn (viewing / waiting), the free manual toggle
  // (`boardView`) drives the surface. Interaction gating (WordTraceLayer /
  // PieceDragLayer) keys off turnPhase independently — this only aligns the
  // VISUAL surface + the manual toggle below.
  const effectiveBoardView: BoardView = isMyTurn
    ? turnPhase === 'word'
      ? 'letters'
      : 'chess'
    : boardView

  // Optimistic staged-move preview — the CHESS view renders an overlaid board
  // so a staged MOVE visibly relocates the piece (source empties, captured
  // opponent piece removed) before the server confirms. The LETTERS view keeps
  // the real playerView; only the chess surface mounts the piece layers, so
  // feeding the overlaid view to MatchCanvas in chess view never affects the
  // letters surface or the DOM overlays (which read `playerView` directly).
  // Gated on effectiveBoardView (turn_phase-driven) so the preview shows during
  // the player's own chess step regardless of the manual toggle.
  const canvasPlayerView =
    effectiveBoardView === 'chess'
      ? chessViewPlayerView(playerView, turn.phaseB)
      : playerView

  // ARCA-619 — the move-drag gesture is live throughout your own chess step,
  // EXCEPT while an empower is being configured (role modal / arming): during
  // arming a board tap must select the piece to empower, so the drag layer
  // yields to MatchGrid. A staged move / empower / powerup no longer blocks the
  // drag — the composite step lets a move coexist with the other slots (and a
  // freshly-empowered piece may move the same turn), so dragging simply
  // (re)stages the move slot without disturbing the others.
  const pieceDragEnabled = isMyTurn && empowerStage === 'idle'

  // ARCA-591 — resolve the ExtendModal's target cell + role. `onInspectPieceTap`
  // only ever sets `extendTargetCell` to an own empowered (non-king) piece, but
  // we re-derive the role here (narrowing role_kind away from null/'king') both
  // for the modal's typed `role` prop and so a stale target from a just-applied
  // server snapshot renders nothing rather than a broken modal.
  const extendCell =
    extendTargetCell !== null && playerView !== null
      ? (playerView.board.find((c) => c.cell_idx === extendTargetCell) ?? null)
      : null
  const extendRole =
    extendCell !== null &&
    extendCell.role_kind !== null &&
    extendCell.role_kind !== 'king'
      ? extendCell.role_kind
      : null

  // The Runespring field is 6 cols × N rows and GROWS mid-match (36→48→60
  // cells → 6/8/10 rows). The board-stack wrapper's aspect-ratio must track
  // cols:rows so the Pixi stage (scaled by WIDTH) + the DOM grid render the
  // non-square board without distortion. Falls back to square (6:6) before
  // the first playerView lands. Re-derives reactively on each WS update.
  // boardRows(0) returns 1 (Math.max(1,...)); use literal 6 for the pre-view
  // square fallback so the aspect ratio is 6:6 before the first WS update.
  const boardRowCount =
    playerView !== null && playerView.board.length > 0
      ? boardRows(playerView.board.length)
      : 6 // square pre-view fallback (6×6)

  // Task 7 — animatable --grow-rows CSS custom property.
  //
  // `growRows` drives `--grow-rows` on the board-stack element via inline style.
  // The CSS @property transition animates the geometry change (6→8→10 rows)
  // over 500 ms whenever the value changes, making the box "breathe" open in
  // lockstep with the letter-cube glide (gated on the $boardReanchor pulse).
  //
  // Rejoin guard: both useState and useRef are initialized to the live values
  // at first render. A rejoining player whose first render already has the live
  // playerView mounts with growRows === boardRowCount, so resolveGrowRows holds.
  // When playerView is still null at first render growRows seeds to the pre-view
  // fallback (6); the grown view then lands with NO reveal cinematic (the
  // reconnect-replay guard in the turn-idx effect suppresses it), so the
  // safety-net snap brings the box to 8/10 instead of leaving it clipped (ARCA-154).
  const [growRows, setGrowRows] = useState<number>(boardRowCount)
  // Tracks the last $boardReanchor epoch we acted on; strictly increases.
  const handledEpochRef = useRef<number>(boardReanchorEpoch)

  // Transition-aware effect: apply resolveGrowRows whenever relevant inputs change.
  // The animated grow is gated on the $boardReanchor epoch (floor-drop beat) so
  // it fires in lockstep with the letter-cube glide; the safety-net snap fires
  // only when the board has grown past the displayed rows AND no reveal cinematic
  // is in flight (see resolveGrowRows). `reveal` is a dep so the effect re-runs
  // when a tap-skipped reveal unmounts without ever pulsing the epoch, letting
  // the safety net settle the geometry.
  useEffect(() => {
    const { nextGrowRows, nextHandledEpoch } = resolveGrowRows({
      boardRowCount,
      growRows,
      prevEpoch: handledEpochRef.current,
      newEpoch: boardReanchorEpoch,
      reducedMotion,
      // reveal !== null covers the cinematic's mounted lifetime; the ref closes
      // the one-render gap before the setReveal lands the commit the board grows.
      growCinematicInFlight:
        reveal !== null || growCinematicInFlightRef.current,
    })
    handledEpochRef.current = nextHandledEpoch
    if (nextGrowRows !== growRows) {
      setGrowRows(nextGrowRows)
    }
  }, [boardRowCount, boardReanchorEpoch, reducedMotion, growRows, reveal])

  // ARCA-155 — measure the real board top and stamp it as `--board-top` (px) on
  // <main> so the fixed-position PowerupTargetSelector overlay aligns its letter
  // grid + instruction pill to the actual board instead of the hardcoded
  // marginTop:144 / top:80 that floated the grid ~one cell-row below it (its
  // bottom row then overlapped the emote bar). Measured — not hardcoded — so it
  // tracks the TopBar height, the device safe-area, and grown boards (the
  // board-stack top is stable across grows, but viewport-height changes and the
  // safe-area can shift it). Re-runs on mount, viewport resize, and whenever the
  // board grows (`growRows`). useLayoutEffect so the value is committed before
  // paint, avoiding a one-frame flash when the overlay first mounts.
  const boardStackRef = useRef<HTMLDivElement>(null)
  const [boardTopPx, setBoardTopPx] = useState<number | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `growRows` is a re-measure trigger (board grows 6→8→10 rows); the effect body intentionally reads only the ref + window.
  useLayoutEffect(() => {
    const el = boardStackRef.current
    if (el === null) return
    const measure = (): void => {
      // getBoundingClientRect().top is viewport-relative (matches the overlay's
      // position:fixed coordinate space); add the board-stack top padding so the
      // overlay grid container lands on board grid row 0.
      setBoardTopPx(el.getBoundingClientRect().top + BOARD_STACK_PAD_TOP)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('resize', measure)
    }
  }, [growRows])

  // ARCA-594 / ARCA-677 — measure the real match chrome (top bar + everything
  // below the board: the affinity rune row + the control deck) and stamp it as
  // `--match-chrome` (px) on <main>, replacing the hardcoded `--match-chrome:
  // 260px` (pieces.css). The board-stack caps its height to
  // 100svh − var(--match-chrome) − safe-area; when the real chrome exceeded the
  // 260px guess (taller chess-phase deck, the coach HintButton, the staged-word
  // readout, a larger safe-area, longer localized labels) the board was
  // over-allocated and its bottom row(s) clipped off-screen at 6×8 / 6×10. The
  // measured reserve tracks the actual chrome across phases (word-trace vs
  // chess-move) and devices, mirroring the `--board-top` pattern above.
  //
  // None of the measured parts depends on the board height (they are flow
  // siblings, not the flexed board), so writing the result back to
  // `--match-chrome` — which only resizes the board — never re-sizes them and so
  // never forms a measure→resize→measure loop. Measurements are rAF-batched so a
  // burst of layout changes (font load, phase swap, tray open) coalesces into a
  // single re-fit instead of thrashing the Pixi canvas. The effect re-runs on
  // the structural deps below (turn/phase swaps the deck content and toggles the
  // affinity row) so the observer set + the synchronous pre-paint measure stay
  // current; the ResizeObserver catches deck-internal height changes (the
  // staged-word chip, the power-up tray) without extra deps. useLayoutEffect so
  // the first value is committed before paint (no one-frame 260px flash).
  const matchRootRef = useRef<HTMLElement>(null)
  const [matchChromePx, setMatchChromePx] = useState<number | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: the deps are re-measure/re-observe triggers (turn/phase swaps the dock, the board view toggles the affinity row, playerView presence gates both); the effect body intentionally reads only the ref + live DOM + window.
  useLayoutEffect(() => {
    const root = matchRootRef.current
    if (root === null) return
    let raf = 0
    const measure = (): void => {
      const topBar = root.querySelector<HTMLElement>('.match-topbar')
      const affinity = root.querySelector<HTMLElement>(
        '[data-screen-region="affinity-chip-row"]',
      )
      const deck = root.querySelector<HTMLElement>(
        '[data-screen-region="control-deck"]',
      )
      const next = resolveMatchChromePx({
        topBarPx: topBar?.offsetHeight ?? 0,
        affinityPx: affinity?.offsetHeight ?? 0,
        deckPx: deck?.offsetHeight ?? 0,
      })
      // Only adopt a real measurement; a null (nothing rendered yet) keeps the
      // CSS 260px fallback rather than collapsing the reserve to 0.
      if (next !== null) setMatchChromePx(next)
    }
    const schedule = (): void => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }
    measure()
    const ro = new ResizeObserver(schedule)
    for (const sel of [
      '.match-topbar',
      '[data-screen-region="affinity-chip-row"]',
      '[data-screen-region="control-deck"]',
    ]) {
      const el = root.querySelector(sel)
      if (el !== null) ro.observe(el)
    }
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', schedule)
    }
  }, [isMyTurn, turnPhase, effectiveBoardView, playerView !== null])

  // Memoize the Pixi overlay sub-layer so its identity is stable
  // across word-phase re-renders (tapping WORD / tracing letters update
  // $turnBuilder, which re-renders MatchScreen but NOT the scene graph). With
  // MatchCanvas wrapped in React.memo, a stable overlay prop + a stable
  // playerView ref let the canvas skip re-rendering entirely on those drafts —
  // the scene graph is a pure function of playerView + boardView, not of the
  // turn-builder draft. (Correctness no longer depends on this — the
  // LetterCubeSpriteMount stable-ref fix already prevents tint stacking — but
  // it avoids re-running ~60 cell diffs per keystroke.)
  const overlayLayerMemo = useMemo(
    () =>
      playerView !== null ? (
        <>
          <AnchorPlacementOverlay
            playerView={playerView}
            matchId={matchId}
            yourSlot={(playerView.your_slot ?? 0) as 0 | 1}
            onPlaceFailure={handleAnchorPlaceFailure}
            onPlaceSuccess={handleAnchorPlaceSuccess}
          />
          {quillMode !== null ? (
            <QuillTargetOverlay
              mode={quillMode}
              playerView={playerView}
              yourSlot={(playerView.your_slot ?? 0) as 0 | 1}
              onTap={onQuillTargetTap}
              onDismiss={() => setQuillMode(null)}
            />
          ) : null}
        </>
      ) : null,
    [
      playerView,
      matchId,
      quillMode,
      handleAnchorPlaceFailure,
      handleAnchorPlaceSuccess,
      onQuillTargetTap,
    ],
  )

  const matchActive =
    matchStatus === null || matchStatus === 'lobby' || matchStatus === 'active'

  // MatchTopBar opponent metadata.
  //
  // TODO(ARCA-126): `opponentName` / `opponentInitial` below are TODO-gated
  // placeholders, NOT real opponent data. Surfacing the real display name (and
  // a pre/post-match rating) requires a backend wire-contract change — adding
  // `opponent_display_name` to PlayerView.opponent_public (PlayerViewSchema.ts)
  // plus the view projection / /api/home — which is deferred to a dedicated
  // backend+web wiring issue (see ARCA-126 "Out of scope"). The new top-bar
  // layout (ARCA-586) ellipsizes/hides the placeholder name gracefully, so it
  // holds when a real, longer name later replaces the stub. `opponentStatus`
  // is derived from the live PlayerView and is real — it drives the localized
  // turn indicator, not the dropped placeholder ELO.
  const opponentName = 'Opponent' // placeholder — see TODO(ARCA-126)
  const opponentInitial = 'O' // placeholder — see TODO(ARCA-126)
  const opponentStatus: 'thinking' | 'played' =
    playerView !== null &&
    playerView.current_player_slot !== playerView.your_slot
      ? 'thinking'
      : 'played'
  // ARCA-152 — the local player's running word score (real wire data, not a
  // placeholder). 0 until the first scored word / while the view is still
  // loading.
  const yourScore = playerView?.your_score ?? 0

  // ARCA-619 — empower playability for the composite chess deck's Empower
  // entry. Empower needs the caller's own non-king (blank) piece on the board
  // AND a word committed this turn (a skip turn cannot empower — the backend's
  // validateEmpower requires source_word_length). When unplayable, the deck
  // renders the button disabled with the localized reason (mirrors the
  // beginEmpower guards, which also surface on a click attempt).
  const empowerDisabledReason: string | null = (() => {
    if (playerView === null) return null
    const hasPiece = playerView.board.some(
      (c) =>
        c.piece_owner_slot === playerView.your_slot && c.piece_kind === 'blank',
    )
    if (!hasPiece)
      return intl.formatMessage({ id: 'match.empower.guard.no_piece' })
    if (committedWordLength() < 1)
      return intl.formatMessage({ id: 'match.empower.guard.no_word' })
    return null
  })()

  const navigateBack = useCallback(() => {
    router.navigate('/lobby')
  }, [router])
  const openMatchSettings = useCallback(() => {
    router.navigate('/settings')
  }, [router])
  const navigateHome = useCallback(() => {
    // ARCA-147 — defer the end-of-first-bot-match push opt-in OFF the WinOverlay
    // and INTO the lobby. On HOME out of a completed bot match (and only while
    // the server one-shot is still unset), hand a sessionStorage baton to
    // LobbyScreen, which surfaces the PushOptInModal on its next mount. This
    // replaces the old synchronous trigger that rendered the modal over the
    // terminal result overlay. LobbyScreen re-checks `pushOptInShown` so the
    // prompt still shows at most once.
    if (matchMode === 'bot' && !onboarding.pushOptInShown) {
      setPendingBotPushOptIn()
    }
    router.navigate('/lobby')
  }, [router, matchMode, onboarding.pushOptInShown])
  const navigateReplay = useCallback(() => {
    router.navigate(`/replay/${matchId}`)
  }, [router, matchId])

  const renderPowerupTargetSelector = (): React.ReactNode => {
    if (powerupTarget === null || playerView === null) return null
    const mySlot = playerView.your_slot
    const opp: 0 | 1 = mySlot === 0 ? 1 : 0
    const commit = (payload: Record<string, number>): void => {
      // INT-07: the cell-targeted power-up STAGES on Confirm — fire the
      // activation whoosh here (not at tray-select, which only opens this
      // selector the user may cancel). ARCA-619: staging only — the composite
      // is committed by the single Submit.
      playSfx('powerup')
      setActivatedPowerup(powerupTarget)
      stagePowerup({ name: powerupTarget, payload })
      setPowerupTarget(null)
    }
    const onCancel = (): void => setPowerupTarget(null)
    if (powerupTarget === 'castle_keep') {
      return (
        <PowerupTargetSelector
          mode="single"
          board={playerView.board}
          titleMessageId="match.powerup_target.castle_keep.title"
          eligible={(c) =>
            c.piece_owner_slot === mySlot &&
            c.piece_kind === 'blank' &&
            c.role_kind === null
          }
          onConfirmSingle={(cellIdx) => commit({ target_cell_idx: cellIdx })}
          onCancel={onCancel}
        />
      )
    }
    if (powerupTarget === 'tongue_tie') {
      return (
        <PowerupTargetSelector
          mode="single"
          board={playerView.board}
          titleMessageId="match.powerup_target.tongue_tie.title"
          eligible={(c) => !c.is_hollow}
          onConfirmSingle={(cellIdx) => commit({ target_cell_idx: cellIdx })}
          onCancel={onCancel}
        />
      )
    }
    if (powerupTarget === 'knights_vow') {
      return (
        <PowerupTargetSelector
          mode="single"
          board={playerView.board}
          titleMessageId="match.powerup_target.knights_vow.title"
          eligible={(c) =>
            c.piece_owner_slot === opp &&
            c.role_kind !== null &&
            c.piece_kind !== 'king'
          }
          onConfirmSingle={(cellIdx) => commit({ cell_idx: cellIdx })}
          onCancel={onCancel}
        />
      )
    }
    // the_vanishing — two-step.
    const inBackRanks = (cellIdx: number): boolean =>
      mySlot === 0
        ? cellIdx >= 0 && cellIdx <= 11
        : cellIdx >= 24 && cellIdx <= 35
    return (
      <PowerupTargetSelector
        mode="vanishing"
        board={playerView.board}
        titleMessageId="match.powerup_target.the_vanishing.title"
        eligibleFrom={(c) =>
          c.piece_owner_slot === mySlot && c.piece_kind !== null
        }
        eligibleTo={(c) =>
          c.piece_owner_slot === null && inBackRanks(c.cell_idx)
        }
        onConfirmVanishing={(fromCellIdx, toCellIdx) =>
          commit({
            from_cell_idx: fromCellIdx,
            to_cell_idx: toCellIdx,
          })
        }
        onCancel={onCancel}
      />
    )
  }

  return (
    <main
      ref={matchRootRef}
      id="main"
      tabIndex={-1}
      className="rg-screen match"
      data-screen="match"
      data-match-id={matchId}
      data-net-status={dataNetStatus}
      data-net-status-raw={status}
      data-turn-idx={playerView?.current_turn_idx ?? 0}
      data-turn-phase={turnPhase}
      // Staged chess move (from-to) once both endpoints are placed, else ''.
      // Mirrors the optimistic phaseB move so the e2e can confirm a pointer-drag
      // actually staged the move before submitting (a sentinel {-1,-1} submit
      // would 409 with phase_b:no_target).
      data-staged-move={(() => {
        const sm = stagedMoveFromPhaseB(turn.phaseB)
        return sm ? `${sm.from}-${sm.to}` : ''
      })()}
      data-match-status={matchStatus ?? ''}
      data-is-my-turn={isMyTurn ? 'true' : 'false'}
      data-requires-anchor-placement={
        playerView?.requires_anchor_placement ? 'true' : 'false'
      }
      // Two-step-turn empower support + ARCA-121 Bug C — the committed word
      // length e2e needs to confirm a word is staged before Submit. Chess step:
      // the caller-only server projection (`your_pending_word_length`, set after
      // the word commit). Word step: the LIVE staged trace length from
      // $turnBuilder.phaseA (reactive via `turn`), so the attribute reflects the
      // staged word during the word step instead of staying empty. Empty string
      // when nothing is staged (no trace / skip Phase A / opponent view).
      data-pending-word-length={
        turnPhase === 'chess'
          ? (playerView?.your_pending_word_length ?? '')
          : turn.phaseA?.kind === 'word' && turn.phaseA.word.length > 0
            ? turn.phaseA.word.length
            : ''
      }
      data-your-tokens={playerView?.your_tokens ?? 0}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100svh',
        maxWidth: 480,
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        backgroundColor: 'var(--color-bg-deep)',
        overflow: 'hidden',
        // #9 — reserve the device safe-area (home indicator / nav bar) so the
        // bottom action dock is never tucked under it.
        paddingBottom: 'env(safe-area-inset-bottom)',
        // ARCA-155 — measured board top (px), inherited by the descendant
        // PowerupTargetSelector overlay so its grid + pill align to the board.
        // Unset until the first measurement lands → the overlay uses its 80px
        // fallback (the layout-effect runs before paint, so this is transient).
        ...(boardTopPx !== null
          ? { ['--board-top' as string]: `${boardTopPx}px` }
          : {}),
        // ARCA-594 / ARCA-677 — measured chrome reserve (top bar + affinity row
        // + control deck), px. Overrides the `.rg-screen.match { --match-chrome:
        // 260px }` fallback in pieces.css once a real measurement lands; unset
        // until then so the fallback holds (the layout-effect runs before paint,
        // so the 260px window is transient).
        ...(matchChromePx !== null
          ? { ['--match-chrome' as string]: `${matchChromePx}px` }
          : {}),
      }}
    >
      <Motes />

      <MatchTopBar
        opponentName={opponentName}
        opponentStatus={opponentStatus}
        opponentInitial={opponentInitial}
        yourScore={yourScore}
        onBackClick={navigateBack}
        onSettingsClick={openMatchSettings}
        hintSlot={
          <HintButton
            realMatchesPlayed={onboarding.realMatchesPlayed}
            onClick={() => {
              void requestHint()
            }}
            loading={hintState.phase === 'loading'}
          />
        }
      />

      {/* Board container — preserves the Phase 14 PixiJS canvas + parallel-DOM
          MatchGrid + WordTrace/PieceDrag overlays. The rg-screen.match shell
          renders these as same-stack siblings; the gameplay layer is
          UNCHANGED from Phase 14. */}
      <div
        ref={boardStackRef}
        data-screen-region="board-stack"
        style={{
          position: 'relative',
          // #9 — fit-to-viewport. As the field grows (6→8→10 rows) the board
          // would otherwise balloon taller than the screen and shove the action
          // dock off-screen on mobile. Cap the board so its height never exceeds
          // the space left after the top bar + dock (--match-chrome) and the
          // safe-area; when that height binds, the WIDTH shrinks (the board
          // scales down) and aspectRatio derives the height. Small boards stay
          // full-width because min() picks 100% first.
          // Task 7 — width and aspectRatio read `var(--grow-rows, 6)` instead
          // of the literal boardRowCount so the CSS @property transition (500 ms
          // cubic-bezier) animates the geometry when --grow-rows changes.
          // MatchScreen drives --grow-rows via the `growRows` state (above) and
          // the transition is gated on the $boardReanchor floor-drop beat.
          //
          // ARCA-749 (ARCA-717 WS3) — the chess view is the FIXED 6×6 board: it
          // never grows on a Flop/River reveal (only the word field deepens). So
          // the board-stack pins to CHESS_ROWS (square 6:6) in the chess view and
          // tracks the growing word field (`growRows`) only in the letters view.
          // Toggling/stepping between views animates the geometry via the same
          // @property transition. effectiveBoardView is turn_phase-driven on your
          // turn and the manual toggle otherwise.
          ['--grow-rows' as string]:
            effectiveBoardView === 'chess' ? CHESS_ROWS : growRows,
          width: `min(100%, calc((100svh - var(--match-chrome) - env(safe-area-inset-bottom)) * ${BOARD_COLS} / var(--grow-rows, 6)))`,
          aspectRatio: `calc(${BOARD_COLS} / var(--grow-rows, 6))`,
          maxWidth: 480,
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '8px 16px 0',
          display: 'grid',
          placeItems: 'center',
          pointerEvents: status === 'error' ? 'none' : 'auto',
        }}
      >
        <div style={{ pointerEvents: 'none', position: 'absolute', inset: 0 }}>
          <MatchCanvas
            playerView={canvasPlayerView}
            boardView={effectiveBoardView}
            // Phase 31 Plan 31-03 Task 3 — AnchorPlacementOverlay mounts as a
            // Pixi sibling of LetterLayer (above letter cubes, below
            // EffectLayer). Self-gates on requires_anchor_placement so the
            // mount is cheap when the flag is false (returns null).
            // Phase 32 Plan 32-04 (RUNE-35) — QuillTargetOverlay shares the
            // same Pixi overlay slot; mounted only when quillMode !== null
            // (set after QuillModePicker.onSelect for Mark/Claim modes;
            // Read mode short-circuits and never lights this overlay).
            // overlayLayer is memoized above (overlayLayerMemo) so its identity
            // is stable across word-phase re-renders; combined with React.memo
            // on MatchCanvas the scene graph skips re-rendering on turn-builder
            // drafts.
            overlayLayer={overlayLayerMemo}
          />
        </div>
        {/* ANCHOR-01/05 — DOM prompt banner that explains the anchor-placement
            gate (what to do + the Hollow trade-off). Renders above the canvas,
            pointer-events:none so the eligible-cell taps reach the Pixi
            AnchorPlacementOverlay beneath. Self-gates on requires_anchor_placement. */}
        <AnchorPlacementPrompt
          requiresAnchorPlacement={
            playerView?.requires_anchor_placement === true
          }
          justPlaced={anchorJustPlaced}
          eligibleCount={
            playerView?.requires_anchor_placement === true
              ? playerView.board.filter(
                  (c) =>
                    c.is_anchor === false &&
                    c.is_hollow === false &&
                    c.letter !== null,
                ).length
              : 0
          }
        />
        {/* #1 — the persistent floating board-view toggle was removed from the
            board stack. During your turn the surface is driven entirely by
            turn_phase (see effectiveBoardView); inspecting both surfaces is only
            meaningful while you wait, so the control now lives with the
            opponent-turn status below (data-screen-region="board-view-inspect"). */}
        {playerView !== null ? (
          <>
            {/* Plan 3 Task 4 — chess interaction (piece drag) is mounted ONLY
                in the chess step (turn_phase==='chess'); in the word step it
                is suppressed so the word-trace owns the board gesture. Legality
                hints + staged-move preview ride PlayerView.your_legal_moves and
                the phaseB staged move (feat/piece-move-legality-hints). */}
            {turnPhase === 'chess' ? (
              <PieceDragLayer
                playerView={playerView}
                onComplete={onPieceDragComplete}
                legalMoves={playerView.your_legal_moves}
                stagedMove={stagedMoveFromPhaseB(turn.phaseB)}
                onCancelMove={() => stageMove(null)}
                onBlankCaptureBlocked={onBlankCaptureBlocked}
                // ARCA-591 — a discrete same-cell tap (no drag) on an own piece
                // is a no-op for moves; route it to the re-empower inspector,
                // which opens the ExtendModal only for own empowered pieces.
                onPieceTap={onInspectPieceTap}
                // The move-drag layer sits at zIndex 4 above MatchGrid and would
                // otherwise capture a piece press as a MOVE. Disable it (→
                // pointer-events:none, yielding to MatchGrid) whenever a non-move
                // Phase B action is selected/configured — both so empower piece-
                // selection works while arming AND so a committed empower /
                // power-up can't be silently clobbered by a stray drag.
                enabled={pieceDragEnabled}
              />
            ) : null}
            {/* MatchGrid is the a11y/keyboard layer; trace renders ABOVE it
                so pointer-drag is captured before MatchGrid's cell buttons.
                The trace layer hits 'pointer-events: none' while phaseA is
                not 'word', so MatchGrid mouse-clicks (Phase B empower / move
                cell-pick) keep working. The chess-view keyboard move FSM keys
                off effectiveBoardView + legalMoves/stagedMove. */}
            <MatchGrid
              playerView={playerView}
              boardView={effectiveBoardView}
              legalMoves={playerView.your_legal_moves}
              stagedMove={stagedMoveFromPhaseB(turn.phaseB)}
              empowerArming={empowerStage === 'arming'}
              onEmpowerPieceTap={onEmpowerPieceTap}
              // ARCA-591 — keyboard re-empower: re-Enter on a picked-up own
              // empowered piece opens the ExtendModal (pointer path is the
              // PieceDragLayer same-cell tap above).
              onInspectPieceTap={onInspectPieceTap}
            />
            {/* Word-trace is enabled only in the word step with a 'word'
                Phase A intent — suppressed entirely during the chess step. */}
            <WordTraceLayer
              playerView={playerView}
              onComplete={onWordTraceComplete}
              enabled={
                turnPhase === 'word' && phaseAValue === 'word' && isMyTurn
              }
            />
          </>
        ) : null}
        {/* Plan 20-07 META-08 — sticker overlay sits inside the board-stack
            so its absolute positioning is bounded by the canvas. Pointer
            events are disabled so it never blocks gameplay input. */}
        <StickerOverlay
          mySlot={mySlot}
          activeStickers={activeStickers}
          onExpire={handleStickerExpire}
        />
      </div>

      {/* Phase 31 Plan 31-04 (RUNE-32) — AffinityChipRow docked above the
          MatchBottomBar. Caller-only consumption (your_affinity_letters +
          your_read_revealed_letters); pulse-on-claim drives Sun-Yellow border
          for 600ms when lastClaimedWordCells contains an affinity letter.
          Note: the per-cell halo (sprite layer) is threaded separately via
          LetterLayer → LetterCubeSpriteMount's `affinityLetters` prop —
          MatchScreen does not pass affinityLetters down directly.

          ARCA-600 — the "Your Runes" row is a word-building aid with no meaning
          in the chess surface, so it is gated to the LETTERS view: present on
          your word step and whenever the opponent-turn toggle shows letters,
          absent on your chess step and whenever that toggle shows chess. Both
          cases collapse to effectiveBoardView (turn_phase-driven on your turn,
          toggle-driven while you wait). */}
      {playerView !== null && effectiveBoardView === 'letters' ? (
        <AffinityChipRow
          playerView={playerView}
          lastClaimedWordCells={lastClaimedWordCells}
        />
      ) : null}

      {/* Plan 3 Task 4 — the visible phase segment is driven by the active
          turn_phase: 'word' shows Phase A (word/skip), 'chess' shows Phase B
          (move/empower/power/pass). The opposite phase's controls are
          suppressed so only the active step is interactive. */}
      {/* Turn-action controls (phase select / power-up tray / Submit) render
          ONLY on the local player's turn. On the opponent's turn the player
          can still watch + inspect the board view (the control sits with the
          "their turn" line below), but must not see or use action controls. */}
      {/* Control-deck footer — take-two MatchScreen.jsx:348-379. The Match
          screen's distinctive pinned action dock: a bottom-up paper→transparent
          gradient fades the board content beneath the controls, and zIndex
          lifts the deck over the board stack. Unlike every other screen (whose
          CTA scrolls with page content), this deck stays at the foot of the
          viewport — the board height is capped via --match-chrome so the dock
          never needs to scroll into view. The home-indicator safe-area inset
          stays on <main> (paddingBottom above) and is NOT duplicated here, so
          the gap is counted exactly once.

          ARCA-602 (WS3) — the control-deck is now the screen's single
          role="contentinfo" landmark (relocated from the deleted always-on
          MatchBottomBar). It is always rendered (both turn branches), so the
          landmark is present in every state including the loading view. The
          lint suppression below records the rationale: a <footer>'s implicit
          contentinfo role only applies at document root, so an explicit role
          is required when the region is nested inside <main>. */}
      {/* biome-ignore lint/a11y/useSemanticElements: explicit role="contentinfo" per UI-SPEC §6.2 — implicit <footer> role only applies at document root, lost when nested in <main>. */}
      <div
        data-screen-region="control-deck"
        role="contentinfo"
        style={{
          position: 'relative',
          zIndex: 6,
          background:
            'linear-gradient(0deg, var(--color-bg-deep) 70%, transparent)',
        }}
      >
        {isMyTurn ? (
          <>
            {/* Phase A (word/skip) is the only segment control. The chess step
                (Phase B) is now COMPOSITE and driven by the ChessControlDeck +
                board surfaces below, not a mutually-exclusive segment. */}
            {turnPhase === 'word' ? (
              <PhaseControls
                phaseA={phaseAValue}
                setPhaseA={applyPhaseA}
                bounceKey={bounceKey}
                style={{ marginTop: 4 }}
              />
            ) : null}

            {/* Swap-panel beneath the controls (ARCA-602 / ARCA-619):
                  • word phase → word-building context: the token bank as a
                    SECONDARY glanceable chip above the staged-word readout.
                  • chess phase → composite chess step: the token bank as the
                    PRIMARY empower/extend currency, the ChessControlDeck
                    (staged-summary + Empower entry), and the always-accessible
                    PowerupTray (no longer gated behind a "power" segment). */}
            <div
              className="phase-panel"
              data-screen-region="phase-panel"
              data-phase={turnPhase}
            >
              {turnPhase === 'word' ? (
                <>
                  {playerView !== null ? (
                    <TokenBank
                      tokens={tokens}
                      tokensCap={5}
                      variant="secondary"
                    />
                  ) : null}

                  {/* ARCA-121 Bug B — staged-word readout above Submit. Driven
                      LIVE off $turnBuilder.phaseA, which WordTraceLayer commits
                      on every pointermove during the drag (commitLivePath), so
                      the running total + token band update as the player traces
                      and then persist as the staged readout on lift (ARCA-597
                      AC#3). It owns the single aria-live score utterance now
                      that the floating ScorePill is gone (AC#5).

                      ARCA-597 — relative wrapper so the relocated on-lift
                      verdict (ScorePillVerdict, formerly over the grid) flashes
                      centered over the chip area near Submit without shifting
                      layout. It is decorative (aria-hidden) and self-dismisses
                      after its lifetime. */}
                  {turn.phaseA?.kind === 'word' &&
                  turn.phaseA.word.length > 0 &&
                  playerView !== null ? (
                    <div style={{ position: 'relative' }}>
                      <StagedWordChip
                        word={turn.phaseA.word}
                        path={turn.phaseA.path}
                        playerView={playerView}
                        callerSlot={mySlot}
                      />
                      {wordVerdict !== null ? (
                        <ScorePillVerdict
                          key={wordVerdict.key}
                          outcome={wordVerdict.outcome}
                          onDone={() => setWordVerdict(null)}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : playerView !== null ? (
                <>
                  <TokenBank tokens={tokens} tokensCap={5} variant="primary" />

                  <ChessControlDeck
                    phaseB={turn.phaseB}
                    playerView={playerView}
                    empowerDisabledReason={empowerDisabledReason}
                    moveConflict={moveEmpowerConflict}
                    onEmpower={beginEmpower}
                    onClearMove={onClearStagedMove}
                    onClearRole={onClearStagedRole}
                    onClearPowerup={onClearStagedPowerup}
                  />

                  {/* ARCA-619 — the power-up tray is always accessible during the
                      chess step (selecting one stages the power-up slot; it
                      composes with a staged move / empower). */}
                  <div className="phase-panel__tray">
                    <PowerupTray
                      playerView={playerView}
                      loadout={playerView.your_powerup_loadout}
                      onSelect={onPowerupSelect}
                    />
                  </div>
                </>
              ) : null}
            </div>

            <div style={{ padding: '10px 16px 24px' }}>
              <Btn
                kind="primary"
                size="lg"
                block
                disabled={submitDisabled}
                testId="turn-submit-btn"
                onClick={onSubmitActivePhase}
              >
                {turnPhase === 'word' ? (
                  <FormattedMessage
                    id="match.cta.submit_word"
                    defaultMessage="SUBMIT WORD"
                  />
                ) : (
                  <FormattedMessage
                    id="match.cta.submit_chess"
                    defaultMessage="Submit Turn"
                  />
                )}
              </Btn>
            </div>
          </>
        ) : (
          <div
            data-screen-region="opponent-turn"
            style={{
              padding: '24px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                color: 'var(--color-text-secondary)',
                fontFamily: 'var(--font-family-body)',
                fontSize: '15px',
              }}
            >
              <FormattedMessage
                id="match.opponent_turn"
                defaultMessage="Opponent's turn — waiting for their move…"
              />
            </div>
            {/* #1 — board-view inspect control (relocated from the floating board
              toggle). Lets the waiting player inspect both the letter grid and
              the chess board while the opponent moves. Reuses the existing
              boardView state + toggleBoardView SFX; effectiveBoardView already
              follows boardView when it is not your turn, so this needs no new
              state. Visible-text labels + aria-pressed give the segmented
              control its accessible names (no new i18n keys). */}
            {/* ARCA-587 — the emote trigger docks beside the board-view inspect
                control: the opponent-turn region is the honest read of "next to
                Submit Move" (Submit only renders on YOUR turn, emotes only on
                the opponent's). Gated on matchActive so it isn't offered after
                the match ends, preserving the old strip's availability.

                ARCA-603 (WS3 §3 matrix / WS4) — this row IS the under-board
                slot's opponent-turn occupant: where PhaseControls + Submit sit
                on your turn, the chess/letter toggle sits while you wait,
                composed with the emote trigger. `flexWrap` + centered
                justification guarantee the two never overlap or clip when the
                row is wider than the slot (e.g. the longer Swedish toggle
                labels "Bokstäver"/"Schack" on a narrow viewport) — they reflow
                onto a second centered line instead of overflowing the deck. */}
            <div
              data-screen-region="opponent-turn-controls"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
              }}
            >
              <div
                data-screen-region="board-view-inspect"
                style={{
                  display: 'inline-flex',
                  gap: 2,
                  padding: 2,
                  borderRadius: 'var(--radius-pill)',
                  border: '1px solid var(--color-surface-deep)',
                  background: 'var(--color-surface-deep)',
                }}
              >
                {(['letters', 'chess'] as const).map((view) => {
                  const active = boardView === view
                  return (
                    <button
                      key={view}
                      type="button"
                      data-board-view-option={view}
                      aria-pressed={active}
                      onClick={() => {
                        if (boardView !== view) toggleBoardView()
                      }}
                      style={{
                        padding: '8px 18px',
                        borderRadius: 'var(--radius-pill)',
                        border: 'none',
                        background: active
                          ? 'var(--color-surface-mid)'
                          : 'transparent',
                        color: active
                          ? 'var(--color-text-primary)'
                          : 'var(--color-text-secondary)',
                        fontFamily: 'var(--font-family-display)',
                        fontSize: 'var(--font-size-13)',
                        fontWeight: 600,
                        cursor: active ? 'default' : 'pointer',
                      }}
                    >
                      {view === 'letters' ? (
                        <FormattedMessage
                          id="match.boardView.chip.letters"
                          defaultMessage="Letters"
                        />
                      ) : (
                        <FormattedMessage
                          id="match.boardView.chip.chess"
                          defaultMessage="Chess"
                        />
                      )}
                    </button>
                  )
                })}
              </div>
              {matchActive ? <EmotePicker onSend={sendEmote} /> : null}
            </div>
          </div>
        )}
      </div>

      {submitError !== null ? (
        // Phase 18 Plan 04 (POLISH-07) — inline aria-live region removed; the
        // assertive announcement now flows through the global <LiveRegion>
        // via useAnnounce() in onSubmit. The visible error banner remains as
        // a visual cue for sighted users.
        //
        // ARCA-145 (Bug B) — `pointer-events: none` is load-bearing: this
        // fixed banner overlaps the Submit button on tall/narrow viewports, and
        // without it the banner swallowed the tap so Submit never fired and the
        // match soft-locked. It carries no interactive controls, so making it
        // transparent to pointers is safe; it auto-dismisses on a timer above.
        <div
          data-screen-region="submit-error"
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            padding: 12,
            backgroundColor: 'var(--color-cta-destructive)',
            color: 'var(--color-text-primary)',
            borderRadius: 'var(--radius-md)',
            textAlign: 'center',
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            zIndex: 1050,
            pointerEvents: 'none',
          }}
        >
          {submitError}
        </div>
      ) : null}

      {/* #7 — transient "why your move was rejected" coach hint (info-toned, not
          the destructive submit-error red). The assertive read is handled by
          announce() in onBlankCaptureBlocked, so this is a visual cue only. */}
      {moveHint !== null ? (
        <div
          data-screen-region="move-hint"
          style={{
            position: 'fixed',
            bottom: 120,
            left: 16,
            right: 16,
            padding: 12,
            background: 'var(--color-surface-mid)',
            color: 'var(--color-text-primary)',
            border: '2px solid var(--color-cta-primary)',
            borderRadius: 'var(--radius-pill)',
            textAlign: 'center',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-14)',
            zIndex: 1050,
          }}
        >
          {moveHint}
        </div>
      ) : null}

      {/* Plan 17-05 — 4 new overlay components. Conditional renders driven
          by existing state machines; the prototype's DOM-overlay shape
          replaces the Phase 14 canvas-based reveal for these moments. */}
      {showEmpower && playerView !== null && isMyTurn ? (
        <EmpowerModal
          onClose={cancelEmpower}
          onConfirm={onEmpowerConfirm}
          {...(empowerArmingRole !== null
            ? { initialSelectedRole: empowerArmingRole }
            : {})}
          roleCosts={playerView.role_costs}
          tokenBalance={tokens}
          // Chess step: the in-memory phaseA was committed + reset, so the
          // committed length comes from the server projection
          // (your_pending_word_length). Word step: the live staged word. Shared
          // with the empower guards via committedWordLength().
          wordLength={committedWordLength()}
        />
      ) : null}
      {/* ARCA-591 — re-empower (extend) modal for an own already-empowered
          piece. Opened by tapping / re-Entering the piece (onInspectPieceTap);
          shows turns-left + extend terms; confirm stages {kind:'extend'} and
          auto-submits the chess step. Gated identically to the empower modal
          (own turn). */}
      {extendCell !== null &&
      extendRole !== null &&
      playerView !== null &&
      isMyTurn ? (
        <ExtendModal
          onClose={onExtendClose}
          onConfirm={onExtendConfirm}
          role={extendRole}
          turnsLeft={Math.max(
            0,
            (extendCell.role_expires_turn ?? playerView.current_turn_idx) -
              playerView.current_turn_idx,
          )}
          terms={playerView.extension_terms ?? { token_cost: 1, turns: 3 }}
          canExtend={extendCell.can_extend}
          tokenBalance={tokens}
        />
      ) : null}
      {/* Empower clarity (role-first flow) — armed-board instruction pill. After
          a role is chosen the board glows the caller's empowerable pieces; this
          banner tells the player to tap one (or cancel). */}
      {empowerStage === 'arming' && empowerArmingRole !== null ? (
        <div
          data-testid="empower-arming-banner"
          role="status"
          style={{
            position: 'fixed',
            top: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-surface-mid)',
            color: 'var(--color-text-primary)',
            border: '2px solid var(--color-cta-primary)',
            borderRadius: 'var(--radius-pill)',
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-14)',
            boxShadow: 'var(--shadow-pop)',
            zIndex: 60,
          }}
        >
          <span>
            {intl.formatMessage(
              { id: 'match.empower.arming.prompt' },
              {
                role: intl.formatMessage({
                  id: `match.empower.role.${empowerArmingRole}`,
                }),
              },
            )}
          </span>
          <Btn kind="ghost" size="sm" onClick={cancelEmpower}>
            <FormattedMessage id="match.empower.arming.cancel" />
          </Btn>
        </div>
      ) : null}
      {/* ARCA-619 — the "discard staged action?" confirm is retired: chess
          slots are now staged independently and cleared per-slot from the
          ChessControlDeck summary (no destructive mutually-exclusive switch). */}
      {/* Phase 31 Plan 31-03 Task 3 — first-time Anchor consumption tutorial.
          Mount gate combines the server one-shot flag with a local session
          ref (latches on first mount) so a cached PlayerView replay can NEVER
          re-mount the modal within this session (T-31-04). */}
      {shouldShowAnchorTutorial ? (
        <FirstAnchorTutorialModal
          onDismiss={() => {
            hasShownAnchorTutorialThisSessionRef.current = true
            setTutorialDismissed(true)
          }}
        />
      ) : null}
      {/* Phase 32 Plan 32-04 (RUNE-35) — Quill 3-mode picker bottom-sheet.
          Mount gate: showQuillPicker (set by onPowerupSelect when the user
          taps the Quill card in PowerupTray). On mode select: Mark/Claim
          routes to QuillTargetOverlay (Pixi); Read short-circuits straight
          to setPhaseB + onSubmit. The QuillTargetOverlay is mounted as a
          Pixi child via the MatchCanvas overlayLayer slot above. */}
      {showQuillPicker ? (
        <QuillModePicker
          onSelect={onQuillModeSelect}
          onDismiss={onQuillDismiss}
        />
      ) : null}
      {/* QuillTargetPrompt — DOM-only banner companion to QuillTargetOverlay.
          Must be mounted OUTSIDE the Pixi <Application> (which lives inside
          the MatchCanvas overlayLayer slot above) — pixi-react's reconciler
          throws on plain text nodes in its tree. v1.2 own-machine UAT
          2026-05-18 discovered the prior single-component QuillTargetOverlay
          had crashed inside the Pixi tree silently, leaving the Quill Mark/
          Claim flow with no visible cell-target highlights or instruction
          banner in production. */}
      {quillMode !== null ? <QuillTargetPrompt mode={quillMode} /> : null}
      {/* Phase 32 Plan 32-05 (RUNE-35) — Mulligan 6-cell selector overlay.
          Mount gate: showMulliganSelector (set by onPowerupSelect when the
          user taps the Mulligan card in PowerupTray). On Confirm the selector
          STAGES the power-up slot (ARCA-619: no auto-submit — the composite is
          committed by the single Submit). On Cancel/Escape the selection is
          discarded with no staging. */}
      {showMulliganSelector && playerView !== null ? (
        <MulliganSelector
          playerView={playerView}
          onSubmit={(cellIndices) => {
            // INT-07: Mulligan stages on the selector's Confirm — fire the
            // activation whoosh here, not at tray-select.
            playSfx('powerup')
            setActivatedPowerup('lexical_mulligan')
            stagePowerup({
              name: 'lexical_mulligan',
              payload: { cell_indices: cellIndices },
            })
            setShowMulliganSelector(false)
          }}
          onDismiss={() => setShowMulliganSelector(false)}
        />
      ) : null}
      {/* Phase 4 Gap 2 (P4) — DOM cell/piece target picker for the four
          cell-targeted power-ups. Eligibility predicates MATCH the backend
          effect files (apps/backend/src/match/powerups/*.ts) so a valid client
          selection is accepted by the authoritative server:
            • castle_keep   — caller-owned, piece_kind==='blank', role_kind===null
                              → payload { target_cell_idx }
            • tongue_tie    — any existing non-Hollow cell
                              → payload { target_cell_idx }
            • knights_vow   — opponent-owned, role_kind!==null, not king
                              → payload { cell_idx }
            • the_vanishing — from = caller-owned piece; to = EMPTY cell on the
                              caller's back two ranks (slot0 0..11, slot1 24..35)
                              → payload { from_cell_idx, to_cell_idx }
          On Confirm: setPhaseB then onSubmitChess (mirrors the Mulligan flow). */}
      {renderPowerupTargetSelector()}
      {/* Phase 2 (court-opens-at-the-center) — FSM-driven Flop + River reveals.
          newCellIndices is the NEW center-inserted cohort for the current grown
          board: backend deepens the MIDDLE, so the new cells are a contiguous
          center window ([18,30) at the flop / [24,36) at the river) — NOT the
          old `>=36`/`>=48` tail (those are SHIFTED existing cells now).
          centerGrowWindow derives the window from board.length; FlopReveal +
          RiverReveal mirror the cascade frontier into $cascadeFrontier so the
          per-cell LetterCubeSprite can self-gate its seam-burst spawn-in. */}
      {flopRevealActive && playerView !== null ? (
        <FlopReveal
          newCellIndices={(() => {
            const w = centerGrowWindow(playerView.board.length)
            return w
              ? Array.from({ length: w.end - w.start }, (_, i) => w.start + i)
              : []
          })()}
          onComplete={dismissReveal}
        />
      ) : null}
      {riverRevealActive && playerView !== null ? (
        <RiverReveal
          newCellIndices={(() => {
            const w = centerGrowWindow(playerView.board.length)
            return w
              ? Array.from({ length: w.end - w.start }, (_, i) => w.start + i)
              : []
          })()}
          onComplete={dismissReveal}
        />
      ) : null}
      {queenRisesActive ? <QueenRises onComplete={dismissQueenRises} /> : null}
      {/* POWERUP-06 — brief activate confirmation for the committed power-up. */}
      {activatedPowerup !== null ? (
        <PowerupActivateOverlay
          name={activatedPowerup}
          onComplete={dismissPowerupActivate}
        />
      ) : null}
      {/* JUICE-03 — token-fly coins arc from the board to the bank counter on
          a tokens-earned claim. Fixed overlay, pointer-events:none, RM-gated. */}
      <TokenFly
        trigger={tokenFly.key}
        count={tokenFly.count}
        originSelector='[data-screen-region="canvas"]'
        targetSelector="[data-token-counter]"
      />
      {isTerminal ? (
        <WinOverlay
          // CINE-1 Task B: outcome is derived from winner_slot (now on the wire)
          // via deriveTerminalOutcome — the SAME value used by the SR announce
          // block so visual and screen-reader text always agree.
          outcome={terminalOutcome}
          onHome={navigateHome}
          onReplay={navigateReplay}
          rewards={{ xp: xpDelta }}
          // CINE-3: no real win-streak source exists on the wire/profile, so
          // the ribbon (shown when streakCount>=2) rode on a fabricated 3.
          // Pass 0 until a backend win_streak field exists.
          streakCount={0}
          matchId={matchId}
          levelUp={levelUp}
          // Phase 31 Plan 31-04 (RUNE-33) — affinity reveal sidecar wiring.
          // The reveal block renders only when BOTH the sidecar atom carries
          // a payload AND we have the caller's slot + own affinity tuple. The
          // spread keeps `exactOptionalPropertyTypes` happy — undefined values
          // are stripped at the spread site, not passed through.
          {...(playerView !== null
            ? {
                yourSlot: playerView.your_slot,
                yourAffinityLetters: playerView.your_affinity_letters,
              }
            : {})}
        />
      ) : null}

      {/* Phase v1.3 (MUI-05 / D-COACH-08/01) — coach-hint surfaces.
          Render-gated on `realMatchesPlayed < 5` (D-COACH-01) AND
          `playerView !== null`. Each hint-phase emits its own surface:
            • findable_word → SVG outline above the board canvas
            • powerup_timing → CoachTip speech-bubble (dismissible)
            • cooldown      → inline aria-live toast
          The HintButton itself is mounted via the MatchTopBar `hintSlot`
          prop below — it gates on the same realMatchesPlayed counter. */}
      {onboarding.realMatchesPlayed < 5 && playerView !== null ? (
        <>
          {hintState.phase === 'findable_word' ? (
            <FindableWordOutline
              cells={hintState.cells}
              cellSize={CELL_SIZE_PX}
            />
          ) : null}
          {hintState.phase === 'powerup_timing' ? (
            <CoachTip
              messageKey={hintState.messageKey}
              onDismiss={dismissHint}
            />
          ) : null}
          {/* First-run contextual hint (tutorial-independent). Shares the
              top-right CoachTip slot with the on-demand powerup_timing/cooldown
              surfaces, so suppress it while either of those occupies the slot. */}
          {firstRunHint !== null &&
          hintState.phase !== 'powerup_timing' &&
          hintState.phase !== 'cooldown' ? (
            <CoachTip
              messageKey={firstRunHint.copyKey}
              onDismiss={dismissFirstRunHint}
            />
          ) : null}
          {hintState.phase === 'cooldown' ? (
            <div
              role="status"
              data-screen-region="coach-hint-cooldown-toast"
              style={{
                position: 'absolute',
                top: 'calc(var(--space-3) * 2 + 44px)',
                right: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                fontFamily: 'var(--font-family-body)',
                fontSize: 13,
                lineHeight: 1.4,
                background: 'var(--color-card-bg)',
                borderRadius: 8,
                // Same coach-surface slot as CoachTip — keep it below the
                // gameplay banners too (ARCA-131). Shared constant.
                zIndex: COACH_TIP_Z_INDEX,
                pointerEvents: 'none',
              }}
            >
              <FormattedMessage id="coach.hint.cooldown_toast" />
            </div>
          ) : null}
        </>
      ) : null}

      <FriendInviteModal
        open={showFriendInvite}
        onDismiss={() => {
          setShowFriendInvite(false)
          const cur = $onboarding.get()
          $onboarding.set({ ...cur, friendInviteShown: true })
        }}
        onShare={() => {
          setShowFriendInvite(false)
          const cur = $onboarding.get()
          $onboarding.set({ ...cur, friendInviteShown: true })
          if (typeof window !== 'undefined') {
            window.location.href = '/profile?invite=true'
          }
        }}
      />
      {/* ARCA-147 — the PushOptInModal no longer renders here. It is deferred
          to LobbyScreen so it never lands over the terminal WIN/LOSE/DRAW
          overlay; see `navigateHome` + the onboarding sessionStorage baton. */}

      {/* Plan 14-08's flat end-of-match banner for non-won terminal states is
          superseded by the outcome-aware <WinOverlay> above (Polish Phase 4 —
          CINE-03/04 / SCREEN-01): lost → 'lose', drawn/stalemate/abandoned/
          timeout/completed → 'draw', all rendering the designed §9.8 end-state
          with the §12.3 edge sweep + matching sting cue. */}
    </main>
  )
}
